import { useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { mapRawTransfersForTable } from "../utils/rawTransferUiRow.js";
import { formatV3TaxPurchaseSubline } from "../utils/v3taxPlanLabel.js";
import MugshotProSubscriptionTypeLabel, {
  formatMugshotProSubscriptionSubline,
} from "./MugshotProSubscriptionTypeLabel.jsx";
import GroncardTypeLabel, {
  formatGroncardSubtitle,
  isGroncardRow,
} from "./GroncardTypeLabel.jsx";
import FourOceanDonationTypeLabel from "./FourOceanDonationTypeLabel.jsx";
import { uiPerfLog } from "../utils/uiPerfLog.js";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t } from "../utils/translations";
import { fmtTokenAmountFixed, formatTxTokenAmount, resolveTokenAmountTooltip } from "../utils/formatNumbers";
import { toast } from "react-toastify";
import { Grid3X3, LayoutDashboard, Lock, Pencil, X } from "lucide-react";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import {
  getNftIconAlt,
  resolveNftIconSrc,
  isVeMugsNft,
  VEMUGS_ICON_SRC,
} from "../utils/nftIcon.js";
import { resolveNftCollectionDisplayName } from "../utils/nftCollections.js";
import { nftBatchDisplayMeta } from "../utils/nftBatchDisplay.js";
import { JUICY_RAW_TYPE_FILTER_OPTIONS, matchesJuicyRawTypeFilter } from "../config/juicyFinanceClassifications.jsx";
import {
  buildManualOverrideSelectGroups,
  collectDatasetClassificationKeys,
  isManualOverrideClassBlocked,
  resolveManualOverridePickerValue,
} from "../config/manualOverrideClassifications.js";
import { validateManualOverrideClassificationForUi } from "../utils/manualOverrideUi.js";
import {
  buildManualOverridePayload,
  pickManualClassification,
} from "../utils/txOverrideKey.js";
import { rowMatchesTaxYear, resolveDisplayTaxYearLabel } from "../utils/taxYear.js";
import { isUkCountry } from "../utils/ukTaxUi.js";
import GasAbstractionFeeLabel from "./GasAbstractionFeeLabel.jsx";
import { getGasAbstractionFeePlainLabel } from "../utils/gasAbstractionFeeLabel.js";
import {
  getVeBetterDaoNavigatorDisplay,
} from "../utils/veBetterDaoNavigatorDisplay.js";
import VaiyansTypeLabel from "./VaiyansTypeLabel.jsx";
import { getVaiyansDisplay } from "../utils/vaiyansDisplay.js";
import ScoopUpTypeLabel from "./ScoopUpTypeLabel.jsx";
import { getScoopUpDisplay } from "../utils/scoopUpDisplay.js";
import VeLotteryTypeLabel from "./VeLotteryTypeLabel.jsx";
import { getVeLotteryDisplay } from "../utils/veLotteryDisplay.js";
import VaultXTypeLabel from "./VaultXTypeLabel.jsx";
import { getVaultXDisplay } from "../utils/vaultxDisplay.js";
import {
  B3MO_ICON_SRC,
  B3MO_ICON_CLASS,
  getB3moDisplay,
  isB3moClass,
} from "../utils/b3moDisplay.js";
import {
  VET_DOMAINS_ICON_SRC,
  getVetDomainsDisplay,
  isVetDomainsClass,
} from "../utils/vetDomainsDisplay.js";
import VetDomainsTypeLabel from "./VetDomainsTypeLabel.jsx";

// SolarWise "Sun Ticket" ERC721 contract
const SOLARWISE_SUNTICKET_CONTRACT = '0x5126a8eec72ed37c5afeae893ffd1bf526bf478f';

// --- LP Token Display Helpers (wie Vip180/VetVtho kompatibel) ---
function isLpPositionToken(token) {
  return typeof token === "string" && (token === "LP_POSITION" || token.startsWith("LP_POSITION:"));
}
function isLpLikeToken(token) {
  const t = String(token || "");
  return isLpPositionToken(t) || t.startsWith("LP_");
}

function isJuicySuppliedToken(token) {
  return String(token || "").toUpperCase().startsWith("JUICY_SUPPLIED:");
}

function extractJuicySuppliedAsset(token) {
  return String(token || "")
    .toUpperCase()
    .replace(/^JUICY_SUPPLIED:/, "")
    .trim();
}

function extractPoolFromLpToken(token, tx) {
  if (token === "LP_POSITION") {
    return tx?.contractAddress || tx?.meta?.poolAddress || tx?.token_contract || null;
  }
  if (token.startsWith("LP_POSITION:")) {
    return token.split("LP_POSITION:")[1] || null;
  }
  return null;
}

// Robust copy with fallback
async function copyToClipboard(text) {
  if (!text) return false;

  // 1) Modern Clipboard API (funktioniert nur in secure context / https / localhost)
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}

  // 2) Fallback: execCommand (funktioniert fast überall)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

const RAW_TYPE_ICON_PROPS = { loading: "lazy", decoding: "async" };

function getGroncardDisplay(tx = {}, clsRaw = "", language = "en") {
  const cls = String(clsRaw || tx?.classification || tx?.type || "")
    .trim()
    .toLowerCase();

  if (cls === "groncard_payment_confirmation") {
    return {
      title: "Groncard",
      subtitle:
        language === "de" ? "Zahlungsbestätigung" : "Payment Confirmation",
      kind: "payment_confirmation",
    };
  }

  if (!isGroncardRow(tx, cls)) {
    return null;
  }

  if (cls === "groncard_gift_card_purchase" || cls === "purchase_cashback") {
    return {
      title: "Groncard",
      subtitle: formatGroncardSubtitle(cls, language),
    };
  }

  return null;
}

// TwoLine Helper-Komponente für harmonisierte 2-Zeilen-Anzeige mit optionaler Pill
function TwoLine({ title, subtitle, icon, colorClass, subtitleClass, bold = false, pillLabel = null, pillKind = null }) {
  const pillColor = pillKind === "IN" 
    ? "bg-green-100 text-green-700 border-green-200" 
    : pillKind === "OUT"
      ? "bg-orange-100 text-orange-700 border-orange-200"
      : "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <div className={`flex items-start gap-1.5 ${colorClass} ${bold ? "font-semibold" : "font-normal"} min-w-0`}>
      {icon && <span className="text-sm flex-shrink-0 mt-0.5 leading-none">{icon}</span>}
      <div className="min-w-0 leading-tight flex-1">
        <div className="whitespace-normal break-words">{title}</div>
        <div className={`whitespace-normal break-words text-[11px] font-medium ${subtitleClass || colorClass + "/90"} flex items-center gap-1.5 flex-wrap`}>
          <span>{subtitle}</span>
          {pillLabel && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${pillColor}`}>
              {pillLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function shortAddr(addr) {
  if (!addr || typeof addr !== "string") return "";
  // Gleiche Länge wie Hash für harmonisches Aussehen
  if (addr.length <= 18) return addr;
  return `${addr.slice(0, 10)}…${addr.slice(-6)}`;
}

function shortHash(h) {
  if (!h || typeof h !== "string") return "";
  if (h.length <= 18) return h;
  return `${h.slice(0, 10)}…${h.slice(-6)}`;
}

function pickHash(tx) {
  return (
    tx?.hash ||
    tx?.txid ||
    tx?.id ||
    tx?.transactionHash ||
    tx?.event_id ||
    tx?.raw_json?.id ||
    ""
  );
}

function pickClauseIndex(tx = {}) {
  const raw =
    tx?.clause_index ??
    tx?.clauseIndex ??
    null;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const n = Number(raw);

  return Number.isInteger(n) && n >= 0 ? n : null;
}

function swapClauseContextKey(tx = {}) {
  const hash = String(pickHash(tx) || "")
    .trim()
    .toLowerCase();
  const clause = pickClauseIndex(tx);

  if (!hash || clause === null) {
    return "";
  }

  return `clause:${hash}:${clause}`;
}

function toUnixSecondsMaybe(x) {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > 1e12) return Math.floor(n / 1000); // ms -> s
  return n;
}

function getTxYear(tx) {
  const ts = Number(tx?.timestamp || 0);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  return new Date(ts * 1000).getFullYear().toString();
}

function toTitleCase(input) {
  const s = String(input || "").trim();
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
    .replace(/\bVebetterdao\b/g, "VeBetterDAO");
}

function isJuicyFinanceClass(cls) {
  return String(cls || "").trim().toLowerCase().startsWith("juicyfinance_");
}

function purchaseCashbackSubtitle(language = "en") {
  return language === "de" ? "Kauf-Cashback" : "Purchase Cashback";
}

function getPauseYourCarbonDisplay(tx = {}, clsRaw = "", language = "en") {
  const cls = String(clsRaw || tx?.classification || tx?.type || "")
    .trim()
    .toLowerCase();
  const application = String(tx?.application || "").trim().toLowerCase();
  const appSlug = String(tx?.x2earnAppSlug || "").trim().toLowerCase();
  const cashbackProgram = String(tx?.cashbackProgram || "").trim().toLowerCase();
  const isReceiptConfirmedPyc =
    tx?.pycFlowUi === true ||
    tx?.pycReceiptVerified === true ||
    String(tx?.pycAppId || "").trim().toLowerCase() ===
      "0xe19c5e83670576cac1cee923e1f92990387bf701af06ff3e0c5f1be8d265c478";

  if (cls === "pause_your_carbon_purchase") {
    return { title: "Pause Your Carbon", subtitle: "Carbon Purchase" };
  }

  if (cls === "pause_your_carbon_carbon_years" || cls === "pause_your_carbon") {
    return { title: "Pause Your Carbon", subtitle: "Carbon Years" };
  }

  const isPycCashback =
    cls === "purchase_cashback" &&
    (
      application === "pause your carbon" ||
      appSlug === "pause_your_carbon" ||
      cashbackProgram === "pause_your_carbon_purchase_rebate" ||
      isReceiptConfirmedPyc
    );

  if (isPycCashback) {
    return {
      title: "Pause Your Carbon",
      subtitle: purchaseCashbackSubtitle(language),
    };
  }

  return null;
}

function getGreencartCashbackDisplay(tx = {}, clsRaw = "", language = "en") {
  const cls = String(clsRaw || tx?.classification || tx?.type || "")
    .trim()
    .toLowerCase();
  if (cls !== "purchase_cashback") return null;

  const application = String(tx?.application || "").trim().toLowerCase();
  const appSlug = String(tx?.x2earnAppSlug || "").trim().toLowerCase();
  const cashbackProgram = String(tx?.cashbackProgram || "").trim().toLowerCase();
  const rewardKind = String(tx?.x2earnRewardKind || "").trim().toLowerCase();
  const isGreencartCardCashback =
    application === "greencart" ||
    appSlug === "greencart" ||
    cashbackProgram === "greencart_card_cashback" ||
    cashbackProgram === "greencart_debit_card" ||
    rewardKind === "greencart_card_cashback";

  return isGreencartCardCashback
    ? { title: "Greencart", subtitle: purchaseCashbackSubtitle(language) }
    : null;
}

function getVetDomainsVnsAirdropDisplay(tx = {}, clsRaw = "", language = "en") {
  const cls = String(clsRaw || tx?.classification || tx?.type || "")
    .trim()
    .toLowerCase();
  const semanticType = String(tx?.providerSemanticType || "").trim().toLowerCase();
  const protocolClass = String(tx?.protocolClassification || "").trim().toLowerCase();
  if (
    cls !== "airdrop" ||
    (semanticType !== "vetdomains_vns_points_distribution" &&
      protocolClass !== "vns_points_distribution")
  ) {
    return null;
  }

  const isExplicitNonIncome =
    tx?.excludeFromTaxIncome === true ||
    (tx?.taxable === false &&
      String(tx?.taxTreatment || "").trim().toLowerCase() === "non_income_airdrop");
  const subtitle = isExplicitNonIncome
    ? language === "de"
      ? "VNS Points Airdrop · kein steuerpflichtiger Zufluss"
      : "VNS Points Airdrop · no taxable income on receipt"
    : "VNS Points Airdrop";

  return { title: "vet.domains", subtitle };
}

const DEX_AGGREGATOR_EXECUTION_CLASSES = new Set([
  // Canonical
  "dex_aggregator_multihop_input",
  "dex_aggregator_multihop_return",
  "dex_aggregator_same_asset_multihop_input",
  "dex_aggregator_same_asset_multihop_return",
  "dex_aggregator_execution_cost",
  "dex_aggregator_fee",

  // Legacy persisted analyses
  "dex_aggregator_roundtrip_parent",
  "dex_aggregator_roundtrip_return",
]);

const DEX_AGGREGATOR_INPUT_CLASSES = new Set([
  "dex_aggregator_multihop_input",
  "dex_aggregator_same_asset_multihop_input",
  "dex_aggregator_roundtrip_parent",
]);

const DEX_AGGREGATOR_RETURN_CLASSES = new Set([
  "dex_aggregator_multihop_return",
  "dex_aggregator_same_asset_multihop_return",
  "dex_aggregator_roundtrip_return",
]);

function formatDexAggregatorTooltipAmount(value, language) {
  const text = Number(value).toFixed(9);
  return language === "de" ? text.replace(".", ",") : text;
}

function getDexAggregatorExecutionDisplay(
  tx = {},
  clsRaw = "",
  language = "en"
) {
  const cls = String(clsRaw || tx?.classification || tx?.type || "")
    .trim()
    .toLowerCase();
  if (!DEX_AGGREGATOR_EXECUTION_CLASSES.has(cls)) return null;

  const title = String(
    tx?.application || tx?.protocolLabel || tx?.dexLabel || tx?.protocol || "DEX Aggregator"
  ).trim();

  let subtitle = "Aggregator Execution Cost";
  if (DEX_AGGREGATOR_INPUT_CLASSES.has(cls)) {
    subtitle =
      tx?.sameAssetStartEnd === false
        ? "Multi-Hop Swap · Input"
        : "Same-Asset Multi-Hop Swap · Input";
  } else if (DEX_AGGREGATOR_RETURN_CLASSES.has(cls)) {
    subtitle =
      tx?.sameAssetStartEnd === false
        ? "Multi-Hop Swap · Return"
        : "Same-Asset Multi-Hop Swap · Return";
  } else if (cls === "dex_aggregator_fee") {
    subtitle = "Aggregator Fee";
  }

  const routeVenueLabel = String(
    tx?.routeVenueLabel ||
      (Array.isArray(tx?.routeVenueLabels) ? tx.routeVenueLabels.join(" → ") : "")
  ).trim();
  const executionRouteLabel = String(tx?.executionRouteLabel || "").trim();
  const inputNotRouted = Number(tx?.inputNotRoutedAmount);
  const routeLoss = Number(tx?.routeExecutionLossAmount);
  const derivedExecutionCost = Number(tx?.derivedExecutionCostAmount);
  const derivedExecutionCostToken = String(
    tx?.derivedExecutionCostToken || tx?.token || tx?.token_symbol || ""
  ).trim().toUpperCase();
  const isDe = language === "de";
  const details = [
    executionRouteLabel
      ? isDe
        ? `Ausführungsroute: ${executionRouteLabel}`
        : `Execution route: ${executionRouteLabel}`
      : "",
    routeVenueLabel
      ? isDe
        ? `Ausführungsplätze: ${routeVenueLabel}`
        : `Execution venues: ${routeVenueLabel}`
      : "",
    Number.isFinite(inputNotRouted) && inputNotRouted > 0
      ? isDe
        ? `Nicht gerouteter Input: ${formatDexAggregatorTooltipAmount(inputNotRouted, language)}`
        : `Unrouted input: ${formatDexAggregatorTooltipAmount(inputNotRouted, language)}`
      : "",
    tx?.sameAssetStartEnd === true && Number.isFinite(routeLoss) && routeLoss > 0
      ? isDe
        ? `Routen-Mengenverlust: ${formatDexAggregatorTooltipAmount(routeLoss, language)}`
        : `Route quantity loss: ${formatDexAggregatorTooltipAmount(routeLoss, language)}`
      : "",
    isDe ? "Receipt-verifiziert" : "Receipt verified",
  ].filter(Boolean);

  return {
    title,
    subtitle,
    icon: tx?.protocolIcon || tx?.dexIcon || (title === "BetterSwap" ? "/Betterswap.png" : ""),
    tone: DEX_AGGREGATOR_RETURN_CLASSES.has(cls) ? "in" : "out",
    tooltip: details.join("\n"),
    derivedExecutionCost:
      DEX_AGGREGATOR_INPUT_CLASSES.has(cls) && derivedExecutionCost > 0
        ? {
            amount: derivedExecutionCost,
            token: derivedExecutionCostToken,
          }
        : null,
  };
}

const HIDE_JUICY_IN_CLEAN_VIEW = new Set([
  "juicyfinance_receipt_mint_burn_info",
  "juicyfinance_receipt_event_candidate",
  "juicyfinance_receipt_backfill_pending",
  "juicyfinance_receipt_backfill_unresolved",
]);

function displayTransferSource(row) {
  if (
    row?.receiptDerived === true ||
    row?.derivedFromWalletDelta === true ||
    row?.source === "RECEIPT_DERIVED"
  ) {
    return "RECEIPT DERIVED";
  }
  if (row?.isSyntheticProtocolEvent || row?.source === "THOR_EVENT_LOG") {
    return "THOR LOG";
  }
  return row?.__source || row?.source || "VIP180";
}

function getJuicyFinanceDisplay(clsRaw, tokenRaw = "") {
  const cls = String(clsRaw || "").trim().toLowerCase();
  const token = String(tokenRaw || "").trim().toUpperCase();

  const prettify = (value) =>
    String(value || "")
      .replace(/_/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");

  const assetOpMatch = cls.match(/^juicyfinance_([a-z0-9]+)_(deposit|withdrawal|borrow|repay)$/i);
  if (assetOpMatch) {
    const [, asset, action] = assetOpMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} ${prettify(action)}`,
    };
  }

  const receiptInMatch = cls.match(/^juicyfinance_([a-z0-9]+)_receipt_transfer_in$/i);
  if (receiptInMatch) {
    const [, asset] = receiptInMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} Position In`,
    };
  }

  const receiptOpeningMatch = cls.match(/^juicyfinance_([a-z0-9]+)_receipt_opening_unknown$/i);
  if (receiptOpeningMatch) {
    const [, asset] = receiptOpeningMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} Opening Position`,
    };
  }

  const receiptOutMatch = cls.match(/^juicyfinance_([a-z0-9]+)_receipt_transfer_out$/i);
  if (receiptOutMatch) {
    const [, asset] = receiptOutMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} Position Out`,
    };
  }

  const receiptInternalMatch = cls.match(
    /^juicyfinance_([a-z0-9]+)_receipt_internal_transfer$/i
  );
  if (receiptInternalMatch) {
    const [, asset] = receiptInternalMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} Internal Position`,
    };
  }

  if (cls === "juicyfinance_receipt_mint_burn_info") {
    return { title: "JuicyFinance", subtitle: "Receipt Mint/Burn (technical)" };
  }

  if (cls === "juicyfinance_receipt_event_candidate") {
    return { title: "JuicyFinance", subtitle: "Receipt Event Candidate" };
  }

  if (cls === "juicyfinance_receipt_backfill_pending") {
    return {
      title: "JuicyFinance",
      subtitle: "Receipt Backfill Pending (needs classification)",
    };
  }

  if (cls === "juicyfinance_receipt_backfill_unresolved") {
    return { title: "JuicyFinance", subtitle: "Receipt Event Review" };
  }

  switch (cls) {
    case "juicyfinance_b3tr_reward_claim":
      return {
        title: "JuicyFinance",
        subtitle: "B3TR Incentive Reward",
      };
    case "juicyfinance_liquidation_collateral_received":
      return {
        title: "JuicyFinance",
        subtitle: "Liquidation Collateral In",
      };
    case "juicyfinance_liquidation_debt_payment":
      return {
        title: "JuicyFinance",
        subtitle: "Liquidation Debt Payment",
      };
    case "juicyfinance_lending_income":
      return { title: "JuicyFinance", subtitle: token ? `${token} Lending Income` : "Lending Income" };
    case "juicyfinance_borrow_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Principal` : "Borrow Principal" };
    case "juicyfinance_repay_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Repay Principal` : "Repay Principal" };
    case "juicyfinance_borrow_interest_paid":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Interest Paid` : "Borrow Interest Paid" };
    case "juicyfinance_unmatched_withdrawal_principal":
      return {
        title: "JuicyFinance",
        subtitle: token
          ? `${token} Withdrawal (Principal unbekannt)`
          : "Withdrawal (Principal unbekannt)",
      };
    default:
      return {
        title: "JuicyFinance",
        subtitle: prettify(cls.replace(/^juicyfinance_/i, "")),
      };
  }
}

function renderJuicyFinanceType(clsRaw, tokenRaw = "") {
  const { title, subtitle } = getJuicyFinanceDisplay(clsRaw, tokenRaw);

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/JF.png"
        alt="JuicyFinance"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        {...RAW_TYPE_ICON_PROPS}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function renderBlueAppType({
  icon,
  alt = "",
  title,
  subtitle,
  titleClass = "text-blue-600",
  subtitleClass = "text-blue-700/90",
  iconClass = "h-5 w-5 object-contain flex-shrink-0 mt-0.5",
}) {
  return (
    <div className={`flex items-start gap-2 font-semibold min-w-0 ${titleClass}`}>
      {icon ? (
        <img
          src={icon}
          alt={alt}
          className={iconClass}
          draggable={false}
          {...RAW_TYPE_ICON_PROPS}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className={`whitespace-normal break-words text-[11px] font-medium ${subtitleClass}`}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function normalizeClassKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function isWovStakingRewardsClass(value) {
  const cls = normalizeClassKey(value);
  return (
    cls === "wov_staking_rewards" ||
    cls === "wov_staking_reward" ||
    cls.startsWith("wov_genesis_staking_reward") ||
    cls.startsWith("genesis_staking_reward") ||
    cls.startsWith("world_of_v_staking_reward") ||
    cls.startsWith("worldofv_staking_reward")
  );
}

function isWovRestakeClass(value) {
  const cls = normalizeClassKey(value);
  return cls === "wov_restake" || cls === "wov_genesis_restake";
}

function isWovClaimFeeClass(value) {
  const cls = normalizeClassKey(value);
  return cls === "wov_claim_fee" || cls === "wov_genesis_claim_fee";
}

function isWovAuctionBidDepositClass(value) {
  const cls = normalizeClassKey(value);
  return (
    cls === "nft_marketplace_vet_bid_deposit" ||
    cls === "nft_marketplace_token_bid_deposit"
  );
}

function isWovAuctionBidRefundClass(value) {
  const cls = normalizeClassKey(value);
  return (
    cls === "nft_marketplace_vet_bid_refund" ||
    cls === "nft_marketplace_token_bid_refund"
  );
}

function getWovAuctionBidSubtitle(classification, language) {
  if (isWovAuctionBidDepositClass(classification)) {
    return language === "de" ? "Auktionsgebot hinterlegt" : "Auction Bid Deposit";
  }

  if (isWovAuctionBidRefundClass(classification)) {
    return language === "de" ? "Auktionsgebot zurückerstattet" : "Auction Bid Refund";
  }

  return "";
}

function renderWovAuctionBidTypeCell(classification, language, renderWithExchangeBadge) {
  if (isWovAuctionBidDepositClass(classification)) {
    return renderWithExchangeBadge(
      renderWorldOfVTypeLabel(
        getWovAuctionBidSubtitle(classification, language),
        "OUT",
        "OUT"
      )
    );
  }

  if (isWovAuctionBidRefundClass(classification)) {
    return renderWithExchangeBadge(
      renderWorldOfVTypeLabel(
        getWovAuctionBidSubtitle(classification, language),
        "IN",
        "IN"
      )
    );
  }

  return null;
}

function renderWorldOfVTypeLabel(subtitle, pillLabel = null, pillKind = null) {
  const pillColor =
    pillKind === "IN"
      ? "bg-green-100 text-green-700 border-green-200"
      : pillKind === "OUT"
        ? "bg-orange-100 text-orange-700 border-orange-200"
        : "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/wov.png"
        alt="World of V"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        {...RAW_TYPE_ICON_PROPS}
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">World of V</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90 flex items-center gap-1.5 flex-wrap">
          <span>{subtitle}</span>
          {pillLabel ? (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${pillColor}`}
            >
              {pillLabel}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function getNftDisplayMeta(tx) {
  const nftName = String(
    resolveNftCollectionDisplayName(tx) ||
      tx?.nftName ||
      tx?.nft_name ||
      tx?.name ||
      tx?.relatedMintNftName ||
      tx?.related_mint_nft_name ||
      ""
  )
    .trim()
    // Never keep a dangling "#" when the token id is missing.
    .replace(/\s*#\s*$/, "");

    const tokenId = (() => {
      const candidates = [
        tx?.tokenId,
        tx?.nftTokenId,
        tx?.nft_token_id,
        tx?.relatedMintTokenId,
        tx?.related_mint_token_id,
        tx?.token_id,
      ];
      for (const value of candidates) {
        if (value === null || value === undefined) continue;
        const s = String(value).trim().replace(/^#/, "");
        if (!s || s === "—" || s === "-") continue;
        return s;
      }

      // Consolidated single VeMugs mint: promote the only batch identity.
      const ids = [];
      const pushId = (value) => {
        if (value === null || value === undefined) return;
        const s = String(value).trim().replace(/^#/, "");
        if (!s || s === "—" || s === "-") return;
        ids.push(s);
      };
      if (Array.isArray(tx?.nftBatchTokenIds)) {
        tx.nftBatchTokenIds.forEach(pushId);
      }
      if (Array.isArray(tx?.nftBatchIdentities)) {
        for (const identity of tx.nftBatchIdentities) {
          pushId(identity?.tokenId ?? identity?.nft_token_id ?? identity?.token_id);
        }
      }
      if (ids.length === 1) return ids[0];
      return "";
    })();

  const batch = nftBatchDisplayMeta(tx);
  const hasName = nftName && nftName !== "—";
  const hasTokenId = Boolean(tokenId);

  let subtitle = "";
  if (batch.isBatch) {
    subtitle = hasName
      ? `${nftName} · Batch ${batch.batchSize}${batch.preview ? ` · ${batch.preview}` : ""}`
      : `Batch ${batch.batchSize}${batch.preview ? ` · ${batch.preview}` : ""}`;
  } else if (hasName && hasTokenId) {
    subtitle = `${nftName} · #${tokenId}`;
  } else if (hasName) {
    subtitle = nftName;
  } else if (hasTokenId) {
    subtitle = `Token ID #${tokenId}`;
  }

  return {
    nftName,
    tokenId,
    subtitle,
    hasMeta: Boolean(subtitle),
    hasName,
    hasTokenId: hasTokenId || Boolean(batch.preview),
    isBatch: batch.isBatch,
    batchSize: batch.batchSize,
    batchPreview: batch.preview,
    batchTokenIds: batch.tokenIds,
  };
}

function getMarketplaceBrand(tx = {}) {
  const marketplace = String(tx?.marketplace || "").trim().toLowerCase();
  const app = String(
    tx?.saleMarketplace ||
      tx?.application ||
      tx?.label ||
      ""
  ).trim();

  if (marketplace === "bvm" || /black\s*ve\s*market|blackvemarket|\bbvm\b/i.test(app)) {
    return "BlackVeMarket";
  }

  if (marketplace === "wov" || /world\s*of\s*v/i.test(app)) {
    return "World of V";
  }

  if (marketplace === "vesea" || /vesea/i.test(app)) {
    return "VeSea";
  }

  if (app && !/^nft(\s+marketplace)?$/i.test(app)) {
    return app;
  }

  return "";
}

function renderNftType({
  title,
  tx,
  colorClass = "text-pink-600",
  subtitleClass = "text-pink-700/90",
  iconSrc,
  alt,
  brand = null,
}) {
  const {
    hasMeta,
    hasName,
    hasTokenId,
    nftName,
    tokenId,
    isBatch,
    batchSize,
    batchPreview,
  } = getNftDisplayMeta(tx);
  const resolvedIconSrc = resolveNftIconSrc(tx, iconSrc);
  const resolvedAlt = alt || getNftIconAlt(tx);
  const resolvedBrand = brand == null ? getMarketplaceBrand(tx) : brand;
  const titleText = isBatch ? `${title} · Batch ${batchSize}` : title;

  const subtitleContent = !hasMeta ? (
    "—"
  ) : isBatch ? (
    <>
      {hasName ? <span className={subtitleClass}>{nftName}</span> : null}
      {hasName && batchPreview ? (
        <span className="text-gray-600 font-normal print:text-gray-800">
          {" "}
          · {batchPreview}
        </span>
      ) : batchPreview ? (
        <span className="text-gray-600 font-normal print:text-gray-800">
          {batchPreview}
        </span>
      ) : (
        <span className={subtitleClass}>{nftName}</span>
      )}
    </>
  ) : hasName && hasTokenId ? (
    <>
      <span className={subtitleClass}>{nftName}</span>
      <span className="text-gray-600 font-normal print:text-gray-800">
        {" "}
        · #{tokenId}
      </span>
    </>
  ) : hasName ? (
    <span className={subtitleClass}>{nftName}</span>
  ) : (
    <span className="text-gray-600 font-normal print:text-gray-800">
      Token ID #{tokenId}
    </span>
  );

  return (
    <div className={`flex items-start gap-2 font-semibold min-w-0 ${colorClass}`}>
      <img
        src={resolvedIconSrc}
        alt={resolvedAlt}
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5 rounded-sm"
        draggable={false}
        {...RAW_TYPE_ICON_PROPS}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        {resolvedBrand ? (
          <div className="whitespace-normal break-words">{resolvedBrand}</div>
        ) : null}
        <div className="whitespace-normal break-words">{titleText}</div>
        <div className="whitespace-normal break-words text-[11px] font-medium">
          {subtitleContent}
        </div>
      </div>
    </div>
  );
}

// --- Liquidity (DEX) Icon Helpers ---
const VEROCKET_ROUTER = "0x576da7124c7bb65a692d95848276367e5a844d95";

const VEROCKET_POOLS = new Set([
  "0x29a996b0ebb7a77023d091c9f2ca34646bea6ede",
]);

const VESWAP_POOLS = new Set([
  "0xf9743d97fc2d2eb34bda6421009113a675df4232",
]);

const BETTERSWAP_POOLS = new Set([
  "0x10e464b31be30262dbac4a8e695baeec5f6d4d63",
]);

const MVA_SWAP_ICON = "/MVA.jpg";
const VAULTX_ICON = "/VAULTX.png";

const MVA_LIQUIDITY_POOLS = new Set([
  "0xccb1aeabc048dbf5a0545a1aa2ab56df112614b5", // MVA Swap (MVA/VET Pool)
]);

const VAULTX_ROUTER = "0xd74d926f11456d04e337694e071d6ce25f30e01d";

const VAULTX_LIQUIDITY_POOLS = new Set([
  "0xb54d6e6d1e67728ae2027e7f570d77c822214839", // VAULTX WOV/VET pool
]);

const norm = (a) => String(a || "").trim().toLowerCase();

function liquidityActionFromTx(tx, classificationOverride = "") {
  const explicit = norm(tx?.liquidityAction);
  if (explicit === "add" || explicit === "remove") return explicit;

  const cls = norm(
    classificationOverride || tx?.classification || tx?.type || ""
  );
  if (
    cls === "liquidity_add_out" ||
    cls === "liquidity_add_lp_in" ||
    cls === "liquidity_refund_in"
  ) {
    return "add";
  }
  if (cls === "liquidity_remove_in" || cls === "liquidity_remove_lp_out") {
    return "remove";
  }
  return "";
}

function liquidityFamilyContextKey(tx, classificationOverride = "") {
  const explicit = String(tx?.liquidityFamilyKey || "").trim();
  if (explicit) return explicit;

  const hash = norm(
    tx?.hash || tx?.txid || tx?.transactionHash || tx?.event_id || ""
  );
  const pair = norm(
    tx?.liquidityPairAddress ||
      tx?.poolAddress ||
      tx?.pairAddress ||
      tx?.pool ||
      tx?.pair ||
      extractPoolFromLpToken(String(tx?.token || ""), tx) ||
      ""
  );
  const action = liquidityActionFromTx(tx, classificationOverride);

  // Ohne Pool darf der alte Hash-Fallback weiterarbeiten. So werden alte
  // Datensätze nicht gebrochen, Multi-Pool-Familien aber niemals vermischt.
  if (!hash || !pair || !action) return "";
  return `${hash}|${pair}|${action}`;
}

function isVaultXDex(key = "", label = "") {
  const k = norm(key);
  const l = norm(label);
  return (
    k === "vaultx" ||
    k === "vault_x" ||
    k === "vault-x" ||
    l === "vaultx" ||
    l === "vault x" ||
    l.includes("vaultx") ||
    l.includes("vault x")
  );
}

function isMvaDex(key = "", label = "") {
  const k = norm(key);
  const l = norm(label);
  return k === "mva" || l === "mva" || l === "mva swap" || l.includes("mva swap");
}

function normalizeLiquidityDexIcon(icon, dexKey = "", label = "") {
  if (isVaultXDex(dexKey, label)) return VAULTX_ICON;
  if (isMvaDex(dexKey, label)) return MVA_SWAP_ICON;
  return icon || null;
}

function makeVaultXDex() {
  return {
    name: "VAULTX",
    icon: VAULTX_ICON,
  };
}

function getLiquidityDexIconClassName(dex) {
  const icon = String(dex?.icon || "");
  const name = String(dex?.name || "");

  if (icon === VAULTX_ICON || isVaultXDex(name, name)) {
    // VAULTX wordmark is wide — fixed height + auto width (not square h/w)
    return "h-4 w-auto max-w-[3rem] object-contain object-left flex-shrink-0 mt-0.5";
  }

  return "h-5 w-5 object-contain flex-shrink-0 mt-0.5 rounded-sm";
}

function makeMvaSwapDex() {
  return {
    name: "MVA Swap",
    icon: MVA_SWAP_ICON,
  };
}

function dexMetaFromBackend(tx) {
  const key = String(
    tx?.liquidityDexKey ||
      tx?.liquidityDex ||
      tx?.dexKey ||
      tx?.dex ||
      ""
  ).toLowerCase();

  const label =
    tx?.liquidityDexLabel ||
    tx?.dexLabel ||
    tx?.dexName ||
    tx?.protocolLabel ||
    tx?.protocol ||
    "";

  const icon = normalizeLiquidityDexIcon(
    tx?.liquidityDexIcon || tx?.dexIcon || tx?.protocolIcon || "",
    key,
    label
  );

  if (isVaultXDex(key, label)) {
    return makeVaultXDex();
  }

  if (isMvaDex(key, label)) {
    return makeMvaSwapDex();
  }

  if (label || icon) {
    return {
      name: label || key || "Liquidity",
      icon,
    };
  }

  return null;
}

function guessDexFromTx(tx) {
  const fromBackend = dexMetaFromBackend(tx);
  if (fromBackend) return fromBackend;

  const sender = norm(
    tx?.sender ||
      tx?.from ||
      tx?.from_address ||
      tx?.source ||
      ""
  );

  const receiver = norm(
    tx?.receiver ||
      tx?.recipient ||
      tx?.to ||
      tx?.to_address ||
      tx?.destination ||
      ""
  );

  if (
    VAULTX_LIQUIDITY_POOLS.has(sender) ||
    VAULTX_LIQUIDITY_POOLS.has(receiver) ||
    sender === VAULTX_ROUTER ||
    receiver === VAULTX_ROUTER
  ) {
    return makeVaultXDex();
  }

  if (MVA_LIQUIDITY_POOLS.has(sender) || MVA_LIQUIDITY_POOLS.has(receiver)) {
    return makeMvaSwapDex();
  }

  if (sender === VEROCKET_ROUTER || receiver === VEROCKET_ROUTER) {
    return { name: "VeRocket", icon: "/VeRocket.png" };
  }

  const pool = norm(
    tx?.pool ||
      tx?.pair ||
      tx?.pairAddress ||
      tx?.poolAddress ||
      tx?.liquidityPairAddress ||
      tx?.liquidity_pair_address ||
      tx?.liquidity_pool ||
      tx?.pair_contract ||
      tx?.contractAddress ||
      tx?.contract ||
      tx?.token_contract ||
      tx?.tokenContract ||
      tx?.emitter ||
      tx?.meta?.poolAddress ||
      tx?.raw_json?.poolAddress ||
      tx?.raw_json?.contract ||
      ""
  );

  if (pool && VAULTX_LIQUIDITY_POOLS.has(pool)) {
    return makeVaultXDex();
  }

  if (pool && MVA_LIQUIDITY_POOLS.has(pool)) {
    return makeMvaSwapDex();
  }

  if (pool && VEROCKET_POOLS.has(pool)) {
    return { name: "VeRocket", icon: "/VeRocket.png" };
  }

  if (pool && VESWAP_POOLS.has(pool)) {
    return { name: "VeSwap", icon: "/VeSwap.jpg" };
  }

  if (pool && BETTERSWAP_POOLS.has(pool)) {
    return { name: "BetterSwap", icon: "/Betterswap.png" };
  }

  return null;
}

// Wir können die Exchange über das LP-Token-Symbol erkennen (kommt aus detectLiquidityEvents.js → token_symbol z.B. BETLP / UNI-V2 / TSV2)
function getDexInfoFromLpSymbol(lpSymbolRaw, poolAddress = null) {
  const sym = String(lpSymbolRaw || "").trim().toUpperCase();
  const poolLower = poolAddress ? String(poolAddress).toLowerCase() : "";

  if (poolLower && VESWAP_POOLS.has(poolLower)) {
    return { name: "VeSwap", icon: "/VeSwap.jpg" };
  }

  if (poolLower && BETTERSWAP_POOLS.has(poolLower)) {
    return { name: "BetterSwap", icon: "/Betterswap.png" };
  }

  if (poolLower && VAULTX_LIQUIDITY_POOLS.has(poolLower)) {
    return makeVaultXDex();
  }

  if (
    sym.includes("VAULTX") ||
    sym.includes("VAULT-X") ||
    sym.includes("VAULT_X")
  ) {
    return makeVaultXDex();
  }

  if (poolLower && MVA_LIQUIDITY_POOLS.has(poolLower)) {
    return makeMvaSwapDex();
  }

  if (
    sym.includes("MVA") ||
    sym.includes("MVALP") ||
    sym.includes("MVA-VET") ||
    sym.includes("MVA/VET")
  ) {
    return makeMvaSwapDex();
  }

  // VeRocket (kann UNI-V2 Symbol verwenden, aber wir identifizieren über spezifische Symbole)
  // VeRocket Router: 0x576da7124C7bB65a692d95848276367e5a844d95
  // VeRocket kann auch "UNI-V2" verwenden, aber wir priorisieren spezifische VeRocket-Symbole
  if (
    sym.includes("VEROCKET") || 
    sym.includes("VR-LP") || 
    sym.includes("VR_LP") ||
    (sym.includes("VR") && !sym.includes("VEX") && !sym.includes("UNI")) // VR aber nicht VEX-V2 oder UNI-V2
  ) {
    return { name: "VeRocket", icon: "/VeRocket.png" };
  }

  // Vexchange (VEX-V2 LP)
  if (sym.includes("VEX-V2") || sym.includes("VEX_V2") || sym.includes("VEXV2")) {
    return { name: "Vexchange", icon: "/Vexchange.png" };
  }

  // BetterSwap
  if (sym.includes("BETLP") || sym.includes("BETTER")) {
    return { name: "Betterswap", icon: "/Betterswap.png" };
  }

  // TurtleSwap
  if (sym.includes("TSV2") || sym.includes("TURTLE")) {
    return { name: "Turtleswap", icon: "/Turtleswap.png" };
  }

  // VeSwap (UNI-V2 style LP) - nur wenn nicht VeRocket
  if (sym.includes("UNI-V2") || sym.includes("UNI_V2") || sym.includes("UNIV2")) {
    return { name: "VeSwap", icon: "/VeSwap.jpg" };
  }

  return null;
}

function renderTokenCell(tx, pdfExportMode = false) {
  const token = String(tx.token || tx.token_symbol || "");

  if (isJuicySuppliedToken(token)) {
    const asset = extractJuicySuppliedAsset(token);

    if (pdfExportMode) {
      return (
        <div className="flex flex-col items-center justify-center leading-tight">
          <span className="text-[10px] font-semibold text-slate-800 print:text-black">
            JF Supplied
          </span>
          <span className="font-mono text-[10px] font-bold text-slate-700 print:text-black">
            {asset}
          </span>
        </div>
      );
    }

    return (
      <div
        className="inline-flex max-w-full flex-col items-center justify-center rounded-xl border border-blue-200 bg-blue-50/70 px-2 py-1 leading-tight shadow-sm"
        title={token}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
          JF Supplied
        </span>
        <span className="font-mono text-xs font-bold text-slate-800">
          {asset}
        </span>
      </div>
    );
  }

  if (!isLpPositionToken(token)) {
    return <span className="font-medium text-gray-800 print:text-black">{token}</span>;
  }

  const pool = extractPoolFromLpToken(token, tx);

  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <span className="inline-flex items-center rounded-full bg-slate-900/5 px-2 py-0.5 text-xs font-semibold text-slate-700">
        LP
      </span>

      {pool && (
        pdfExportMode ? (
          <div className="text-[10px] leading-tight text-gray-700">
            <div className="font-semibold tracking-wide">POOL:</div>
            <div className="font-mono text-[10px] text-black pdf-break-all">{pool}</div>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="whitespace-nowrap">Pool:</span>
            <button
              type="button"
              className="font-mono hover:text-gray-700 underline-offset-2 hover:underline cursor-copy"
              title={pool}
              onClick={(e) => {
                e.stopPropagation();
                copyAddressWithToast(pool);
              }}
            >
              {shortAddr(pool)}
            </button>
          </div>
        )
)}
    </div>
  );
}


export default function RawTransfersTable({
  year = "",
  displayTaxYear,
  selectedCountry = "DE",
  vetVthoTxs = [],
  vip180Txs = [],
  manualClassifications = {},
  pdfExportMode = false,
  pdfExportVariant = "calc",

  isEditMode = false,
  isPreviewMode = false,
  onToggleEditMode = null,
  pendingOverrideCount = 0,
  savedOverrideCount = 0,
  onClassificationChange = () => {},
  onEditDetected = () => {},
}) {
  const { language } = useLanguage();
  const tone = analysisTone.sky;
  const canToggleEditMode = typeof onToggleEditMode === "function";
  const countryCode = String(selectedCountry || "DE").trim().toUpperCase().replace(/\s+/g, "_");
  const isUK = isUkCountry(countryCode);
  const isIT = countryCode === "IT";
  const classifiedLedgerSubtitle =
    language === "de"
      ? isUK
        ? "Klassifiziert für das Steuerjahr — Basis für UK Section 104 Matching und Export."
        : isIT
          ? "Klassifiziert für das Steuerjahr — Basis für LIFO und Export."
          : "Klassifiziert für das Steuerjahr — Basis für FIFO und Export."
      : isUK
        ? "Classified for the tax year — basis for UK Section 104 matching and export."
        : isIT
          ? "Classified for the tax year — basis for LIFO and export."
          : "Classified for the tax year — basis for FIFO and export.";

  const getDisplayedClassification = (tx) =>
    pickManualClassification(manualClassifications, tx) ||
    tx.manualClassification ||
    tx.classification ||
    tx.type ||
    "";

  const getPickerClassification = (tx) =>
    resolveManualOverridePickerValue(getDisplayedClassification(tx) || "auto");

  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--sky";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--sky";

  const selectedYear = String(year || "").trim();
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);
  const hasSelectedYear = selectedYear !== "";

  // Web: standardmäßig nur aktuelles Steuerjahr zeigen
  // PDF: Historie bleibt vollständig (Year-Filter wird unten für pdfExportMode deaktiviert)
  const [showOnlySelectedYear, setShowOnlySelectedYear] = useState(Boolean(selectedYear));

  useEffect(() => {
    setShowOnlySelectedYear(Boolean(selectedYear));
  }, [selectedYear]);

  const numberLocale = language === "de" ? "de-DE" : "en-US";
  const formatCount = (value) => new Intl.NumberFormat(numberLocale).format(Number(value || 0));

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [tokenFilter, setTokenFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const tableScrollRef = useRef(null);
  const [tableViewCompact, setTableViewCompact] = useState(false);
  const RAW_ROW_HEIGHT = 68;

  // Wichtig: RawTransfer-Rows sind schwer.
  // Hoher Overscan rendert zu viele komplexe Zellen pro Scroll-Frame.
  const RAW_VIRTUAL_OVERSCAN = 10;

  // ~70px effektive Zeilenhöhe → xl/2xl zeigen ca. 18–20 TX gleichzeitig.
  // Overscan bleibt niedrig; nur die sichtbare Viewport-Höhe wächst.
  const rawTableScrollHeightClass = tableViewCompact
    ? "h-[640px]"
    : "h-[720px] md:h-[900px] lg:h-[1040px] xl:h-[1240px] 2xl:h-[1400px]";

  // ✅ NEU: Datenquelle Toggles (statt Main/Delegate + statt Source Dropdown)
  const [showVetVthoData, setShowVetVthoData] = useState(true);
  const [showVip180Data, setShowVip180Data] = useState(true);

  // 1) Merge + Normalisierung (schlanke UI-Rows, kein {...tx})
  const merged = useMemo(() => {
    const t0 = performance.now();
    const a = mapRawTransfersForTable(vetVthoTxs, "VETVTHO");
    const b = mapRawTransfersForTable(vip180Txs, "VIP180");
    const rows = [...a, ...b];

    // Some persisted/older datasets do not yet contain every new PYC metadata
    // field. The receipt-confirmed legs still share one transaction hash, so
    // propagate the PYC UI context from its payment/CY leg to the B3TR cashback.
    const pycFlowHashes = new Set(
      rows
        .filter((row) => {
          const cls = String(row?.classification || row?.type || "").toLowerCase();
          return (
            cls === "pause_your_carbon_purchase" ||
            cls === "pause_your_carbon_carbon_years" ||
            cls === "pause_your_carbon" ||
            String(row?.application || "").trim().toLowerCase() === "pause your carbon"
          );
        })
        .map((row) => String(pickHash(row) || "").trim().toLowerCase())
        .filter(Boolean)
    );

    const displayRows = rows.map((row) => {
      const hash = String(pickHash(row) || "").trim().toLowerCase();
      return hash && pycFlowHashes.has(hash) ? { ...row, pycFlowUi: true } : row;
    });
    uiPerfLog("RawTransfers merge", {
      mergedCount: displayRows.length,
      durationMs: Math.round(performance.now() - t0),
    });
    return displayRows;
  }, [vetVthoTxs, vip180Txs]);

  const manualOverrideSelectGroups = useMemo(
    () =>
      buildManualOverrideSelectGroups({
        language,
        datasetClassKeys: collectDatasetClassificationKeys(merged),
      }),
    [language, merged]
  );

  const manualOverrideOptionValues = useMemo(() => {
    const values = new Set();
    for (const group of [
      manualOverrideSelectGroups.tier1,
      manualOverrideSelectGroups.tierWrap,
      manualOverrideSelectGroups.tier2Static,
      manualOverrideSelectGroups.tier2Dynamic,
    ]) {
      for (const opt of group) values.add(opt.value);
    }
    return values;
  }, [manualOverrideSelectGroups]);

  const rowsByTxid = useMemo(() => {
    const m = new Map();
    for (const tx of merged) {
      const txid = String(tx?.hash || tx?.txid || tx?.transactionHash || tx?.event_id || "").toLowerCase();
      if (!txid) continue;
      if (!m.has(txid)) m.set(txid, []);
      m.get(txid).push(tx);
    }
    return m;
  }, [merged]);

  const sortedMerged = useMemo(
    () => [...merged].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
    [merged]
  );

  // ✅ Liquidity Context: pro TXID das LP-Symbol merken (BETLP / UNI-V2 / TSV2 ...)
  // Damit können wir bei ALLEN liquidity_* Rows (auch VET/VTHO Underlying) das passende DEX-Icon zeigen.
  const lpSymbolByTxid = useMemo(() => {
    const m = new Map();
    for (const tx of merged) {
      const txid = String(tx?.hash || tx?.txid || tx?.transactionHash || "").toLowerCase();
      if (!txid) continue;

      const token = String(tx?.token || "");
      if (!isLpPositionToken(token)) continue; // nur LP_POSITION:... Rows sind verlässlich

      const sym = String(tx?.token_symbol || tx?.tokenSymbol || "").trim();
      if (!sym) continue;

      if (!m.has(txid)) m.set(txid, sym);
    }
    return m;
  }, [merged]);

  // Multi-Pool-Transaktionen brauchen ihren LP-/DEX-Kontext pro
  // Hash+Pair+Action-Familie. Ein reiner TXID-Key würde z. B. eine
  // VeSwap->BetterSwap-Migration vollständig als BetterSwap anzeigen.
  const lpSymbolByLiquidityFamily = useMemo(() => {
    const m = new Map();
    for (const tx of merged) {
      const cls = String(
        pickManualClassification(manualClassifications, tx) ||
          tx?.classification ||
          tx?.type ||
          ""
      ).toLowerCase();
      if (!cls.includes("liquidity")) continue;

      const familyKey = liquidityFamilyContextKey(tx, cls);
      if (!familyKey || m.has(familyKey)) continue;
      if (!isLpPositionToken(String(tx?.token || ""))) continue;

      const sym = String(tx?.token_symbol || tx?.tokenSymbol || "").trim();
      if (sym) m.set(familyKey, sym);
    }
    return m;
  }, [merged, manualClassifications]);

  const dexByLiquidityFamily = useMemo(() => {
    const m = new Map();
    for (const tx of merged) {
      const cls = String(
        pickManualClassification(manualClassifications, tx) ||
          tx?.classification ||
          tx?.type ||
          ""
      ).toLowerCase();
      if (!cls.includes("liquidity")) continue;

      const familyKey = liquidityFamilyContextKey(tx, cls);
      if (!familyKey || m.has(familyKey)) continue;

      const dex = guessDexFromTx(tx);
      if (dex) m.set(familyKey, dex);
    }
    return m;
  }, [merged, manualClassifications]);

  // ✅ DEX pro TXID: erkennt DEX (VeRocket etc.) für alle Liquidity-Zeilen einer TXID
  // Wichtig: Eine TXID kann mehrere Zeilen haben (LP + Underlying), aber nur eine hat evtl. Pool-Adresse
  const dexByTxid = useMemo(() => {
    const m = new Map();

    for (const tx of merged) {
      const txid = String(tx?.hash || tx?.txid || tx?.transactionHash || "").toLowerCase();
      if (!txid) continue;

      const cls = String(
        pickManualClassification(manualClassifications, tx) || tx.classification || tx.type || ""
      ).toLowerCase();

      // nur für liquidity relevant (damit nichts anderes beeinflusst wird)
      if (!cls.includes("liquidity")) continue;

      // wenn schon gesetzt, skip
      if (m.has(txid)) continue;

      // 1) Versuche direkt aus dieser Zeile
      let dex = guessDexFromTx(tx);

      // 2) Wenn nicht gefunden: Zeilen derselben TXID (O(1) Lookup)
      if (!dex) {
        const siblings = rowsByTxid.get(txid) || [];
        for (const t2 of siblings) {
          dex = guessDexFromTx(t2);
          if (dex) break;
        }
      }

      if (dex) m.set(txid, dex);
    }

    return m;
  }, [merged, manualClassifications, rowsByTxid]);

  // ✅ Swap Context: pro TXID die Swap-Richtung (Token OUT -> Token IN) sammeln
  const swapCtxByTxid = useMemo(() => {
    const m = new Map();

    for (const tx of merged) {
      const txid = String(tx?.hash || "").toLowerCase();
      if (!txid) continue;

      const cls = String(
        pickManualClassification(manualClassifications, tx) || tx.classification || tx.type || ""
      ).toLowerCase();

      const isIn = cls === "swap_in" || cls.includes("swap_in");
      const isOut = cls === "swap_out" || cls.includes("swap_out");
      if (!isIn && !isOut) continue;

      const token = String(tx.token || tx.token_symbol || tx.tokenSymbol || "")
        .trim()
        .toUpperCase();
      if (!token) continue;

      let entry = m.get(txid);
      if (!entry) {
        entry = { ins: new Set(), outs: new Set() };
        m.set(txid, entry);
      }

      if (isIn) entry.ins.add(token);
      if (isOut) entry.outs.add(token);
    }

    const out = new Map();
    for (const [txid, entry] of m.entries()) {
      out.set(txid, { ins: Array.from(entry.ins), outs: Array.from(entry.outs) });
    }
    return out;
  }, [merged, manualClassifications]);

  // ✅ Swap Pair Context
  //
  // Priorität:
  // 1. explizite swap_pair_id
  // 2. Hash + Clause
  // 3. reiner Hash NUR wenn der Hash genau ein eindeutiges Token-Paar enthält
  const swapPairByTxid = useMemo(() => {
    const groups = new Map();

    const addToGroup = (key, tx) => {
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(tx);
    };

    const pickClassLower = (tx) =>
      String(
        pickManualClassification(manualClassifications, tx) ||
          tx?.classification ||
          tx?.type ||
          ""
      )
        .trim()
        .toLowerCase();

    const pickToken = (tx) =>
      String(tx?.token || tx?.token_symbol || tx?.tokenSymbol || "")
        .trim()
        .toUpperCase();

    for (const tx of merged) {
      const cls = pickClassLower(tx);
      const isSwapIn = cls === "swap_in" || cls.includes("swap_in");
      const isSwapOut = cls === "swap_out" || cls.includes("swap_out");
      if (!isSwapIn && !isSwapOut) continue;

      const hash = String(pickHash(tx) || "")
        .trim()
        .toLowerCase();
      if (!hash) continue;

      const explicitPairId = String(
        tx?.swap_pair_id || tx?.swapPairId || ""
      ).trim();
      if (explicitPairId) {
        addToGroup(`pair:${explicitPairId}`, tx);
      }

      const clauseKey = swapClauseContextKey(tx);
      if (clauseKey) {
        addToGroup(clauseKey, tx);
      }

      addToGroup(`hash:${hash}`, tx);
    }

    const result = new Map();

    for (const [key, list] of groups.entries()) {
      const outTokens = new Set();
      const inTokens = new Set();

      for (const tx of list) {
        const cls = pickClassLower(tx);
        const token = pickToken(tx);
        if (!token) continue;

        if (cls === "swap_out" || cls.includes("swap_out")) {
          outTokens.add(token);
        }
        if (cls === "swap_in" || cls.includes("swap_in")) {
          inTokens.add(token);
        }
      }

      if (outTokens.size !== 1 || inTokens.size !== 1) continue;

      const from = Array.from(outTokens)[0];
      const to = Array.from(inTokens)[0];
      if (!from || !to || from === to) continue;

      result.set(key, { from, to });
    }

    return result;
  }, [merged, manualClassifications]);

  // Tokens: LP wird als "LP" zusammengefasst
  const availableTokens = useMemo(() => {
    const tokens = new Set();
    merged.forEach((tx) => {
      const tok = String(tx.token || "");
      if (!tok) return;
      if (isLpLikeToken(tok)) tokens.add("LP");
      else tokens.add(tok.toUpperCase());
    });
    return Array.from(tokens).sort();
  }, [merged]);

  // Filterlogik ohne Jahresfilter (Basis) — auf vor-sortierter Liste
  const filteredBase = useMemo(() => {
    const t0 = performance.now();
    const q = deferredSearch.trim().toLowerCase();
    const tokenFilterUpper = String(tokenFilter || "ALL").toUpperCase();
    const typeFilterLower = String(typeFilter || "ALL").toLowerCase();

    const out = sortedMerged.filter((tx) => {
      const effectiveClassLower = normalizeClassKey(
        pickManualClassification(manualClassifications, tx) || tx.classification || tx.type || ""
      );

      if (HIDE_JUICY_IN_CLEAN_VIEW.has(effectiveClassLower)) {
        return false;
      }

      const isThorLog =
        tx.__source === "THOR LOG" ||
        tx.__source === "THOR_EVENT_LOG" ||
        tx.isSyntheticProtocolEvent === true;

      // Datenquelle Filter
      const matchDataset =
        tx.__source === "VETVTHO"
          ? showVetVthoData
          : tx.__source === "VIP180" || isThorLog
            ? showVip180Data
            : true;

      // Token Filter (LP => "LP")
      const currentTok = String(tx.token || "");
      const effectiveTok = isLpLikeToken(currentTok) ? "LP" : currentTok.toUpperCase();
      const matchToken = tokenFilterUpper === "ALL" || effectiveTok === tokenFilterUpper;

      // Type Filter
      let matchType = false;
      if (typeFilterLower === "all") {
        matchType = true;
      } else {
        const internalMatch =
          typeFilterLower === "internal_transfer" &&
          (effectiveClassLower.startsWith("internal_transfer") ||
            (effectiveClassLower.includes("internal") && effectiveClassLower.includes("transfer")) ||
            !!tx.internal_flag);

        const refundMatch =
          typeFilterLower === "refund" &&
          (effectiveClassLower === "refund" ||
            effectiveClassLower.startsWith("refund") ||
            !!tx.refund ||
            !!tx.refundOfHash ||
            !!tx.refund_of_hash);

        if (internalMatch || refundMatch) {
          matchType = true;
        } else {
          switch (typeFilterLower) {
            case "stargate_reward":
            case "stargate_staking_rewards":
              matchType = effectiveClassLower.includes("stargate") && effectiveClassLower.includes("staking");
              break;
            case "stargate_boosted_maturity":
              matchType = effectiveClassLower === "stargate_boosted_maturity";
              break;
            case "thorhead_reward":
            case "thorhead_staking_rewards":
              matchType = effectiveClassLower.includes("thorhead") && effectiveClassLower.includes("staking");
              break;
            case "vemugs_staking_rewards":
              matchType = effectiveClassLower === "vemugs_staking_rewards";
              break;
            case "vemugs_mint_reward":
              matchType = effectiveClassLower === "vemugs_mint_reward";
              break;
            case "vemugs_reward_distribution":
              matchType = effectiveClassLower === "vemugs_reward_distribution";
              break;
            case "vemugs_mint_gas_refund":
              matchType = effectiveClassLower === "vemugs_mint_gas_refund";
              break;
            case "vemugs_mint_zero_transfer":
              matchType = effectiveClassLower === "vemugs_mint_zero_transfer";
              break;
            case "solarwise_electricity_revenue":
              matchType = effectiveClassLower === "solarwise_electricity_revenue";
              break;
            case "solarwise_stake_deposit":
              matchType = effectiveClassLower === "solarwise_stake_deposit";
              break;
            case "solarwise_stake_withdrawal":
              matchType = effectiveClassLower === "solarwise_stake_withdrawal";
              break;
            case "vet_stake_deposit":
              matchType = effectiveClassLower === "vet_stake_deposit";
              break;
            case "vet_stake_withdrawal":
              matchType = effectiveClassLower === "vet_stake_withdrawal";
              break;
            case "vexchange_farm_rewards":
              matchType = effectiveClassLower === "vexchange_farm_rewards";
              break;
            case "wov_genesis_staking_rewards":
            case "wov_genesis_staking_reward":
            case "wov_staking_rewards":
            case "wov_staking_reward":
            case "genesis_staking_rewards":
            case "genesis_staking_reward":
              matchType = isWovStakingRewardsClass(effectiveClassLower);
              break;
            case "wov_restake":
            case "wov_genesis_restake":
              matchType = isWovRestakeClass(effectiveClassLower);
              break;
            case "wov_claim_fee":
            case "wov_genesis_claim_fee":
              matchType = isWovClaimFeeClass(effectiveClassLower);
              break;
            case "pause_your_carbon_carbon_years":
            case "pause_your_carbon":
              matchType = Boolean(getPauseYourCarbonDisplay(tx, effectiveClassLower));
              break;
            case "betterswap_pool2earn_rewards":
              matchType = effectiveClassLower === "betterswap_pool2earn_rewards";
              break;
            case "mva_staking_rewards":
              matchType = effectiveClassLower === "mva_staking_rewards";
              break;
            case "listen2earn_rewards":
              matchType = effectiveClassLower === "listen2earn_rewards";
              break;
            case "wanchain_bridge_to_earn_reward":
              matchType = effectiveClassLower === "wanchain_bridge_to_earn_reward";
              break;
            case "bangzboardz_staking_reward":
              matchType = effectiveClassLower === "bangzboardz_staking_reward";
              break;
            case "goatz_milk_nft_holder_reward":
              matchType = effectiveClassLower === "goatz_milk_nft_holder_reward";
              break;
            case "b3mo":
            case "b3mo_challenge_escrow_deposit":
            case "b3mo_challenge_principal_return":
            case "b3mo_challenge_payout_reward":
            case "b3mo_split_win_reward":
              matchType =
                typeFilterLower === "b3mo"
                  ? isB3moClass(effectiveClassLower) ||
                    String(tx.x2earnRewardKind || "").toLowerCase() === "b3mo_quest" ||
                    String(tx.subApplication || "").toLowerCase().includes("b3mo")
                  : effectiveClassLower === typeFilterLower;
              break;
            case "vet_domains":
            case "vet_domains_registration":
            case "vet_domains_renewal":
            case "vet_domains_payment_refund":
              matchType =
                typeFilterLower === "vet_domains"
                  ? isVetDomainsClass(effectiveClassLower)
                  : effectiveClassLower === typeFilterLower;
              break;
            case "vaultx_loan_borrow":
            case "vaultx_collateral_deposit":
            case "vaultx_collateral_return":
            case "vaultx_loan_principal_repayment":
            case "vaultx_loan_repayment_fee":
            case "vaultx_loan_repayment_refund":
            case "vaultx_auction_bid_deposit":
            case "vaultx_auction_bid_refund":
            case "vaultx_protocol_outflow_review":
            case "vaultx_nft_return_review":
            case "vaultx":
              matchType =
                typeFilterLower === "vaultx"
                  ? effectiveClassLower.startsWith("vaultx_")
                  : effectiveClassLower === typeFilterLower;
              break;
            case "airdrop":
            case "x2earn_reward":
            case "x2earn_reward_claim":
            case "x2earn_reward_distribution":
              matchType =
                effectiveClassLower === "airdrop" ||
                effectiveClassLower === "x2earn_reward" ||
                effectiveClassLower === "x2earn_reward_claim" ||
                effectiveClassLower === "x2earn_reward_distribution" ||
                effectiveClassLower.includes("x2earn");
              break;
            case "verocket_generated_vtho_claim":
              matchType = effectiveClassLower === "verocket_generated_vtho_claim";
              break;
            case "swap_in":
              matchType = effectiveClassLower === "swap_in" || effectiveClassLower.includes("swap_in");
              break;
            case "swap_out":
              matchType = effectiveClassLower === "swap_out" || effectiveClassLower.includes("swap_out");
              break;
            case "swap_refund_in":
              matchType =
                effectiveClassLower === "swap_refund_in" ||
                effectiveClassLower.includes("swap_refund_in");
              break;
            case "squad_game_spend":
              matchType =
                effectiveClassLower === "squad_game_spend" ||
                effectiveClassLower.includes("squad_game_spend");
              break;
            case "squad_vtho_game_entry":
              matchType = effectiveClassLower === "squad_vtho_game_entry";
              break;
            case "vaiyans_vco_game_entry":
            case "vaiyans_vco_prediction_entry":
            case "vaiyans_vco_leaderboard_reward":
            case "vaiyans_vco_tip":
              matchType = effectiveClassLower === typeFilterLower;
              break;
            case "scoopup_puppy_pass_fee":
              matchType = effectiveClassLower === "scoopup_puppy_pass_fee";
              break;
            case "one_million_pixel_purchase":
              matchType =
                effectiveClassLower ===
                "one_million_pixel_purchase";
              break;
            case "velottery_ticket_purchase":
              matchType = effectiveClassLower === "velottery_ticket_purchase";
              break;
            case "squad_game_reward":
              matchType =
                effectiveClassLower === "squad_game_reward" ||
                effectiveClassLower.includes("squad_game_reward");
              break;
            case "squad_nft_upgrade_spend":
              matchType =
                effectiveClassLower === "squad_nft_upgrade_spend" ||
                effectiveClassLower.includes("squad_nft_upgrade_spend");
              break;
            case "squad_nft_upgrade_burn":
              matchType =
                effectiveClassLower === "squad_nft_upgrade_burn" ||
                effectiveClassLower.includes("squad_nft_upgrade_burn");
              break;
            case "squad_nft_upgrade_mint":
              matchType =
                effectiveClassLower === "squad_nft_upgrade_mint" ||
                effectiveClassLower.includes("squad_nft_upgrade_mint");
              break;
            case "squad_preico_payment":
              matchType =
                effectiveClassLower === "squad_preico_payment" ||
                effectiveClassLower.includes("squad_preico_payment");
              break;
            case "squad_preico_forward":
              matchType =
                effectiveClassLower === "squad_preico_forward" ||
                effectiveClassLower.includes("squad_preico_forward");
              break;
            case "squad_preico_claim":
              matchType =
                effectiveClassLower === "squad_preico_claim" ||
                effectiveClassLower.includes("squad_preico_claim");
              break;
            case "golden_empire_reward":
              matchType =
                effectiveClassLower === "golden_empire_reward" ||
                effectiveClassLower.includes("golden_empire_reward");
              break;
            case "golden_empire_spend":
              matchType =
                effectiveClassLower === "golden_empire_spend" ||
                effectiveClassLower.includes("golden_empire_spend");
              break;
            case "golden_empire_burn_spend":
              matchType =
                effectiveClassLower === "golden_empire_burn_spend" ||
                effectiveClassLower.includes("golden_empire_burn_spend");
              break;
            case "nfbc_purchase":
              matchType =
                effectiveClassLower === "nfbc_purchase" ||
                effectiveClassLower.includes("nfbc_purchase");
              break;
            case "liquidity_add":
              matchType = effectiveClassLower.startsWith("liquidity_add");
              break;
            case "liquidity_remove":
              matchType = effectiveClassLower.startsWith("liquidity_remove");
              break;
            case "received":
              matchType =
                effectiveClassLower === "received" &&
                !effectiveClassLower.includes("swap") &&
                !effectiveClassLower.includes("stargate") &&
                !effectiveClassLower.includes("thorhead") &&
                !effectiveClassLower.includes("solarwise") &&
                !effectiveClassLower.includes("airdrop") &&
                !effectiveClassLower.includes("mint");
              break;
            case "sent":
              matchType =
                effectiveClassLower === "sent" &&
                !effectiveClassLower.includes("swap") &&
                !effectiveClassLower.includes("mint") &&
                !effectiveClassLower.includes("stargate") &&
                !effectiveClassLower.includes("thorhead") &&
                !effectiveClassLower.includes("solarwise") &&
                !effectiveClassLower.includes("fee");
              break;
            default:
              if (matchesJuicyRawTypeFilter(effectiveClassLower, typeFilterLower)) {
                matchType = true;
              } else {
                matchType = effectiveClassLower === typeFilterLower;
              }
          }
        }
      }

      const matchSearch =
        q === "" ||
        [
          currentTok,
          effectiveTok,
          tx.hash || "",
          tx.sender || "",
          tx.receiver || "",
          tx.from || "",
          tx.to || "",
          String(tx.amount ?? ""),
          String(tx.amount_raw ?? ""),
          String(tx.amount_display ?? ""),
          tx.type || "",
          tx.classification || "",
          tx.__source || "",
        ].some((field) => String(field).toLowerCase().includes(q));

      return matchDataset && matchToken && matchType && matchSearch;
    });
    uiPerfLog("RawTransfers filter", {
      filteredCount: out.length,
      durationMs: Math.round(performance.now() - t0),
    });
    return out;
  }, [
    sortedMerged,
    deferredSearch,
    tokenFilter,
    typeFilter,
    manualClassifications,
    showVetVthoData,
    showVip180Data,
  ]);

  // Web: optional Steuerjahr; PDF: immer volle Historie (Reihenfolge bereits stabil)
  const filtered = useMemo(() => {
    if (pdfExportMode || !showOnlySelectedYear || !hasSelectedYear) {
      return filteredBase;
    }
    return filteredBase.filter((tx) =>
      rowMatchesTaxYear(tx, selectedCountry, Number(selectedYear))
    );
  }, [filteredBase, pdfExportMode, showOnlySelectedYear, hasSelectedYear, selectedYear, selectedCountry]);

  const rowVirtualizer = useVirtualizer({
    count: pdfExportMode ? 0 : filtered.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => RAW_ROW_HEIGHT,
    overscan: RAW_VIRTUAL_OVERSCAN,
    getItemKey: (index) => {
      const tx = filtered[index];

      return [
        tx?.__source || "",
        tx?.hash || "",
        tx?.token || tx?.token_symbol || tx?.tokenSymbol || "",
        tx?.timestamp || tx?.block_timestamp || tx?.ts || "",
        tx?.type || tx?.classification || "",
        index,
      ].join(":");
    },
    isScrollingResetDelay: 120,
  });

  const hiddenByYearCount = useMemo(() => {
    if (pdfExportMode || !showOnlySelectedYear || !hasSelectedYear) return 0;
    return Math.max(0, filteredBase.length - filtered.length);
  }, [pdfExportMode, filteredBase, filtered, showOnlySelectedYear, hasSelectedYear]);

  const copyAddressWithToast = async (addr) => {
    const ok = await copyToClipboard(addr);
    if (ok) toast.success(language === "de" ? "Adresse kopiert!" : "Address copied!");
    else toast.error(language === "de" ? "Kopieren fehlgeschlagen" : "Copy failed");
  };

  const copyHashWithToast = async (hash) => {
    const ok = await copyToClipboard(hash);
    if (ok) toast.success(language === "de" ? "Hash kopiert!" : "Hash copied!");
    else toast.error(language === "de" ? "Kopieren fehlgeschlagen" : "Copy failed");
  };

  // renderTypeCell: VetVtho-Style
  function renderTypeCell(tx) {
    const classification = (pickManualClassification(manualClassifications, tx) || tx.classification || tx.type || "").trim();

    const isExchangeLinked = tx.flags?.includes?.("linked_exchange");
    const exchangeName = tx.exchange_source || tx.exchange || null;

    const renderWithExchangeBadge = (classificationElement) => {
      if (!isExchangeLinked || !exchangeName) return classificationElement;

      return (
        <div className="flex flex-col gap-0.5">
          {classificationElement}
          <span className="text-xs text-gray-500 font-normal">
            {exchangeName.charAt(0).toUpperCase() + exchangeName.slice(1).toLowerCase()}
          </span>
        </div>
      );
    };

    const clsLower = classification.toLowerCase();
    if (pdfExportMode) {
      const label = (() => {
        if (clsLower === "gas_abstraction_fee") {
          return getGasAbstractionFeePlainLabel(language);
        }
        const navigatorDisplay = getVeBetterDaoNavigatorDisplay(tx, clsLower, language);
        if (navigatorDisplay) {
          return `${navigatorDisplay.title} · ${navigatorDisplay.subtitle}`;
        }
        if (classification.startsWith("NFT_Stargate_")) return classification;
        if (clsLower === "nft_mint" || clsLower === "mint_payment") {
          if (isVeMugsNft(tx)) {
            const meta = getNftDisplayMeta(tx);
            if (meta.isBatch) {
              return `VeMugs · NFT Mint · Batch ${meta.batchSize}${
                meta.batchPreview ? ` · ${meta.batchPreview}` : ""
              }`;
            }
            const tokenId =
              tx.tokenId ??
              tx.relatedMintTokenId ??
              tx.nft_token_id ??
              tx.token_id ??
              null;
            return tokenId
              ? `VeMugs · NFT Mint · #${tokenId}`
              : "VeMugs · NFT Mint";
          }

          const { subtitle, hasMeta } = getNftDisplayMeta(tx);
          return hasMeta ? `NFT Mint · ${subtitle}` : "NFT Mint";
        }
        if (clsLower === "nft_marketplace_purchase_payment") {
          const { subtitle, hasMeta } = getNftDisplayMeta(tx);
          const title = language === "en" ? "NFT Purchase" : "NFT-Kauf";
          const brand = getMarketplaceBrand(tx);
          const head = brand ? `${brand} · ${title}` : title;
          return hasMeta ? `${head} · ${subtitle}` : head;
        }
        if (clsLower === "sale_payment" || clsLower === "sale") {
          const { subtitle, hasMeta } = getNftDisplayMeta(tx);
          return hasMeta ? `NFT Sale · ${subtitle}` : "NFT Sale";
        }
        if (clsLower === "stargate_staking_rewards") return "Stargate Staking Rewards";
        if (clsLower === "stargate_boosted_maturity") return "Stargate Boosted Maturity";
        if (clsLower === "thorhead_staking_rewards") return "Thorhead Staking Rewards";
        if (clsLower === "vemugs_staking_rewards") return "VeMugs Staking Rewards";
        if (clsLower === "vemugs_mint_reward") return "VeMugs Mint Reward";
        if (clsLower === "vemugs_reward_distribution") {
          return tx.vemugsRewardMode === "treasury_post_brew_reward" ||
            tx.subApplication === "Treasury Reward" ||
            tx.providerSuggestedLabel === "VeMugs Treasury Reward"
            ? "VeMugs Treasury Reward"
            : "VeMugs Reward Distribution";
        }
        if (clsLower === "vemugs_mint_gas_refund") return "VeMugs Mint Gas Refund";
        if (clsLower === "vemugs_mint_zero_transfer") {
          const meta = getNftDisplayMeta(tx);
          return meta.isBatch
            ? `VeMugs · Mint · 0 VTHO Placeholder · Batch ${meta.batchSize}${
                meta.batchPreview ? ` · ${meta.batchPreview}` : ""
              }`
            : "VeMugs · Mint · 0 VTHO Placeholder";
        }
        if (isWovStakingRewardsClass(clsLower)) return "World of V Staking Rewards";
        if (clsLower === "nft_creator_royalty") return "World of V Creator Royalty";
        if (isWovRestakeClass(clsLower)) return "World of V Restake";
        if (isWovClaimFeeClass(clsLower)) return "World of V Claim Fee";
        if (isWovAuctionBidDepositClass(clsLower)) {
          return language === "de"
            ? "World of V Auktionsgebot hinterlegt"
            : "World of V Auction Bid Deposit";
        }
        if (isWovAuctionBidRefundClass(clsLower)) {
          return language === "de"
            ? "World of V Auktionsgebot zurückerstattet"
            : "World of V Auction Bid Refund";
        }
        const vnsAirdropDisplay = getVetDomainsVnsAirdropDisplay(tx, clsLower, language);
        if (vnsAirdropDisplay) {
          return `${vnsAirdropDisplay.title} · ${vnsAirdropDisplay.subtitle}`;
        }
        const groncardDisplay = getGroncardDisplay(tx, clsLower, language);
        if (groncardDisplay) {
          return `${groncardDisplay.title} · ${groncardDisplay.subtitle}`;
        }
        const greencartCashbackDisplay = getGreencartCashbackDisplay(tx, clsLower, language);
        if (greencartCashbackDisplay) {
          return `${greencartCashbackDisplay.title} - ${greencartCashbackDisplay.subtitle}`;
        }
        const pycDisplay = getPauseYourCarbonDisplay(tx, clsLower, language);
        if (pycDisplay) return `${pycDisplay.title} · ${pycDisplay.subtitle}`;
        const aggregatorDisplay = getDexAggregatorExecutionDisplay(tx, clsLower, language);
        if (aggregatorDisplay) {
          return `${aggregatorDisplay.title} · ${aggregatorDisplay.subtitle}`;
        }
        if (clsLower === "vexchange_farm_rewards") return "Vexchange Farm Rewards";
        if (clsLower === "betterswap_pool2earn_rewards") return "BetterSwap Pool2Earn Rewards";
        if (clsLower === "mva_staking_rewards") return "MVA Staking Rewards";
        if (clsLower === "listen2earn_rewards") return "Listen2Earn Rewards";
        if (clsLower === "wanchain_bridge_to_earn_reward") {
          return "Wanchain Bridge-to-Earn Reward · Cross-Chain Task Reward";
        }
        if (clsLower === "bangzboardz_staking_reward") {
          return "BangzBoardz Staking Reward · Paladins / NFT Staking";
        }
        if (clsLower === "goatz_milk_nft_holder_reward") {
          return "Goatz Club · MILK NFT Holder Reward";
        }
        const b3moDisplay = getB3moDisplay(clsLower, tx);
        if (b3moDisplay) {
          return `${b3moDisplay.title} · ${b3moDisplay.subtitle}`;
        }
        const vetDomainsDisplay = getVetDomainsDisplay(clsLower, tx);
        if (vetDomainsDisplay) {
          return `${vetDomainsDisplay.title} · ${vetDomainsDisplay.subtitle}`;
        }
        const vaultxDisplay = getVaultXDisplay(clsLower);
        if (vaultxDisplay) {
          return `${vaultxDisplay.title} · ${vaultxDisplay.subtitle}`;
        }
        if (
          clsLower === "airdrop" ||
          clsLower === "x2earn_reward" ||
          clsLower === "x2earn_reward_claim" ||
          clsLower === "x2earn_reward_distribution"
        ) {
          return "X2Earn Reward · Claim";
        }
        if (clsLower === "safehaven_staking_rewards") return "Safehaven Staking Rewards";
        if (clsLower === "sagaz_reward_claim") return "Sagaz Reward Claim";
        if (clsLower === "vfox_nft_holder_reward") return "VFA NFT Holder Reward";
        if (clsLower === "vimworld_veed_reward_distribution") return "VIMworld Reward Distribution";
        if (clsLower === "vimworld_veed_deposit") return "VIMworld VEED Deposit";
        if (clsLower === "vimworld_veed_withdrawal") return "VIMworld VEED Withdrawal";
        if (clsLower === "vimworld_veed_feed_fee") return "VIMworld VEED Feed Fee";
        if (clsLower === "vimworld_veed_withdrawal_fee") return "VIMworld VEED Withdrawal Fee";
        if (clsLower === "vesea_nft_staking_rewards") return "VeSea NFT Staking Rewards";
        if (clsLower === "union_nft_holder_rewards") return "UNION NFT Holder Rewards";
        if (clsLower.startsWith("solarwise_")) return classification.replace(/^solarwise_/i, "Solarwise ").replace(/_/g, " ");
        if (clsLower === "nfbc_purchase") return language === "de" ? "NFBC Kauf" : "NFBC Purchase";
        if (clsLower === "mugshot_pro_subscription") {
          return `Mugshot · ${formatMugshotProSubscriptionSubline(tx, language)}`;
        }
        if (clsLower === "vewin_4ocean_donation") {
          return language === "de" ? "4Ocean · Spende" : "4Ocean · Donation";
        }
        if (clsLower === "one_million_pixel_purchase") {
          return "1Million Pixel – Pixel Purchase";
        }
        if (clsLower === "v3tax_purchase") return language === "de" ? "V3Tax Kauf" : "V3Tax purchase";
        if (clsLower === "received") return t("received", language);
        if (clsLower === "sent") return t("sent", language);
        if (clsLower === "swap_in") return "Swap In";
        if (clsLower === "swap_out") return "Swap Out";
        if (clsLower === "swap_refund_in") return "Swap Refund In";
        const vaiyansPdfDisplay = getVaiyansDisplay(clsLower);
        if (vaiyansPdfDisplay) {
          return `${vaiyansPdfDisplay.title} - ${vaiyansPdfDisplay.subtitle}`;
        }
        const scoopUpPdfDisplay = getScoopUpDisplay(clsLower);
        if (scoopUpPdfDisplay) {
          return `${scoopUpPdfDisplay.title} - ${scoopUpPdfDisplay.subtitle}`;
        }
        const veLotteryPdfDisplay = getVeLotteryDisplay(clsLower);
        if (veLotteryPdfDisplay) {
          return `${veLotteryPdfDisplay.title} - ${veLotteryPdfDisplay.subtitle}`;
        }
        if (clsLower === "squad_game_spend") {
          return language === "de" ? "Squirtle Squad – Game Spend" : "Squirtle Squad – Game Spend";
        }
        if (clsLower === "squad_vtho_game_entry") {
          return language === "de"
            ? "Squirtle Squad – VTHO Game Entry"
            : "Squirtle Squad – VTHO Game Entry";
        }
        if (clsLower === "squad_game_reward") {
          return language === "de" ? "Squirtle Squad – Game Reward" : "Squirtle Squad – Game Reward";
        }
        if (clsLower === "squad_nft_upgrade_spend") {
          return language === "de"
            ? "Squirtle Squad – NFT Upgrade Spend"
            : "Squirtle Squad – NFT Upgrade Spend";
        }
        if (clsLower === "squad_nft_upgrade_burn") {
          return language === "de"
            ? "Squirtle Squad – NFT Upgrade Burn"
            : "Squirtle Squad – NFT Upgrade Burn";
        }
        if (clsLower === "squad_nft_upgrade_mint") {
          return language === "de"
            ? "Squirtle Squad – NFT Upgrade Mint"
            : "Squirtle Squad – NFT Upgrade Mint";
        }
        if (clsLower === "squad_preico_payment") {
          return language === "de"
            ? "Squirtle Squad – Pre-ICO Pool"
            : "Squirtle Squad – Pre-ICO Pool";
        }
        if (clsLower === "squad_preico_forward") {
          return language === "de"
            ? "Squirtle Squad – Pre-ICO Pool Forward"
            : "Squirtle Squad – Pre-ICO Pool Forward";
        }
        if (clsLower === "squad_preico_claim") {
          return language === "de"
            ? "Squirtle Squad – Pre-ICO Claim"
            : "Squirtle Squad – Pre-ICO Claim";
        }
        if (clsLower === "golden_empire_reward") return "Golden Empire Rewards";
        if (clsLower === "golden_empire_spend") return "Golden Empire Spend";
        if (clsLower === "golden_empire_burn_spend") return "Golden Empire Burn Spend";
        if (clsLower === "token_approval") return "Token Approval";
        if (clsLower === "swap_proceeds_sent") return language === "de" ? "Swap-Ergebnis verwendet" : "Swap Proceeds Spent";
        if (clsLower === "liquidity_add") return "Add Liquidity (LP)";
        if (clsLower === "liquidity_remove") return "Remove Liquidity (LP)";
        if (clsLower === "internal_transfer") return language === "de" ? "Interner Transfer" : "Internal Transfer";
        if (clsLower === "refund") return "Refund";
        if (clsLower === "wanchain_bridge_burn_out") {
          return language === "de" ? "WanBridge Bridge Burn Out" : "WanBridge Bridge Burn Out";
        }
        if (clsLower === "wanchain_bridge_network_fee") {
          return language === "de" ? "WanBridge Bridge Network Fee" : "WanBridge Bridge Network Fee";
        }
        if (isJuicyFinanceClass(clsLower)) {
          const { title, subtitle } = getJuicyFinanceDisplay(
            clsLower,
            tx?.token || tx?.token_symbol || tx?.tokenSymbol || tx?.symbol || tx?.asset || ""
          );
          return subtitle ? `${title} ${subtitle}` : title;
        }
        // default: Title Case
        return classification
          .replace(/_/g, " ")
          .split(" ")
          .filter(Boolean)
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");
      })();
      // PDF: niemals Typen abschneiden (NFT_Stargate_* kann sehr lang sein)
      return renderWithExchangeBadge(
        <span className="font-medium text-black pdf-break-all">{label}</span>
      );
    }

    const vnsAirdropDisplay = getVetDomainsVnsAirdropDisplay(tx, clsLower, language);
    const vetDomainsDisplay = vnsAirdropDisplay || getVetDomainsDisplay(clsLower, tx);
    if (vetDomainsDisplay) {
      return renderWithExchangeBadge(
        vnsAirdropDisplay ? (
          renderBlueAppType({
            icon: VET_DOMAINS_ICON_SRC,
            alt: "vet.domains",
            title: vnsAirdropDisplay.title,
            subtitle: vnsAirdropDisplay.subtitle,
          })
        ) : (
          <VetDomainsTypeLabel classification={clsLower} tx={tx} />
        )
      );
    }

    const navigatorDisplay = getVeBetterDaoNavigatorDisplay(tx, clsLower, language);
    if (navigatorDisplay) {
      const isOutflow = navigatorDisplay.tone === "out";
      return renderWithExchangeBadge(
        <div
          className={`flex items-start gap-2 font-semibold min-w-0 ${
            isOutflow ? "text-orange-600" : "text-green-600"
          }`}
        >
          <img
            src="/b3tr-icon.png"
            alt="B3TR"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">{navigatorDisplay.title}</div>
            <div
              className={`whitespace-normal break-words text-[11px] font-medium ${
                isOutflow ? "text-orange-700/90" : "text-green-700/90"
              }`}
            >
              {navigatorDisplay.subtitle}
            </div>
          </div>
        </div>
      );
    }

    const groncardDisplay = getGroncardDisplay(tx, clsLower, language);
    if (groncardDisplay) {
      return renderWithExchangeBadge(
        <GroncardTypeLabel
          tx={tx}
          classification={clsLower}
          language={language}
        />
      );
    }

    const greencartCashbackDisplay = getGreencartCashbackDisplay(tx, clsLower, language);
    if (greencartCashbackDisplay) {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Greencart.png"
            alt="Greencart"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Greencart</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              {greencartCashbackDisplay.subtitle}
            </div>
          </div>
        </div>
      );
    }

    const pycDisplay = getPauseYourCarbonDisplay(tx, clsLower, language);
    if (pycDisplay) {
      const isPycOutflow = clsLower === "pause_your_carbon_purchase";
      return renderWithExchangeBadge(
        <div
          className={`flex items-start gap-2 font-semibold min-w-0 ${
            isPycOutflow ? "text-orange-600" : "text-green-600"
          }`}
        >
          <img
            src="/carbon.png"
            alt="Pause Your Carbon"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Pause Your Carbon</div>
            <div
              className={`whitespace-normal break-words text-[11px] font-medium ${
                isPycOutflow ? "text-orange-700/90" : "text-green-700/90"
              }`}
            >
              {pycDisplay.subtitle}
            </div>
          </div>
        </div>
      );
    }

    const aggregatorDisplay = getDexAggregatorExecutionDisplay(tx, clsLower, language);
    if (aggregatorDisplay) {
      const isInflow = aggregatorDisplay.tone === "in";
      const derivedCost = aggregatorDisplay.derivedExecutionCost;
      const derivedCostText = derivedCost
        ? language === "de"
          ? `Abgeleitete Kosten: ${fmtTokenAmountFixed(derivedCost.amount, 6)} ${derivedCost.token} · kein zusätzlicher Transfer`
          : `Derived cost: ${fmtTokenAmountFixed(derivedCost.amount, 6)} ${derivedCost.token} · not an additional transfer`
        : "";
      return renderWithExchangeBadge(
        <div
          className={`flex items-start gap-2 font-semibold min-w-0 ${
            isInflow ? "text-green-600" : "text-orange-600"
          }`}
          title={aggregatorDisplay.tooltip || undefined}
        >
          {aggregatorDisplay.icon ? (
            <img
              src={aggregatorDisplay.icon}
              alt={aggregatorDisplay.title}
              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
              draggable={false}
              {...RAW_TYPE_ICON_PROPS}
            />
          ) : null}
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">
              {aggregatorDisplay.title}
            </div>
            <div
              className={`whitespace-normal break-words text-[11px] font-medium ${
                isInflow ? "text-green-700/90" : "text-orange-700/90"
              }`}
            >
              {aggregatorDisplay.subtitle}
            </div>
            {derivedCost ? (
              <div
                className="mt-0.5 whitespace-normal break-words text-[10px] font-semibold leading-tight text-indigo-700"
                title={
                  language === "de"
                    ? "Aus dem receipt-verifizierten Unterschied zwischen gesendetem und zurückerhaltenem Betrag berechnet. Diese Zeile ist keine dritte Blockchain-Bewegung."
                    : "Calculated from the receipt-verified difference between the sent and returned amounts. This is not a third blockchain movement."
                }
              >
                ↳ {derivedCostText}
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    if (clsLower === "wanchain_bridge_burn_out") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/WAN.png",
          alt: "WanBridge",
          title: "WanBridge",
          subtitle: language === "de" ? "Bridge Burn Out" : "Bridge Burn Out",
        })
      );
    }

    if (clsLower === "wanchain_bridge_network_fee") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/WAN.png",
          alt: "WanBridge",
          title: "WanBridge",
          subtitle: language === "de" ? "Bridge Network Fee" : "Bridge Network Fee",
        })
      );
    }

    // WoV Auction bid escrow — vor allen generischen nft_*-Fallbacks
    const wovAuctionBidCell = renderWovAuctionBidTypeCell(
      classification,
      language,
      renderWithExchangeBadge
    );
    if (wovAuctionBidCell) return wovAuctionBidCell;

    if (classification.startsWith("NFT_Stargate_")) {
      // Extrahiere Tier und Typ (deposit/withdrawal) aus der Klassifikation
      // Format: NFT_Stargate_{Tier}_{deposit|withdrawal}
      const match = classification.match(/^NFT_Stargate_(.+?)_(deposit|withdrawal)$/i);
      
      if (match) {
        const [, tierRaw, type] = match;
        const typeDisplay = type === "deposit" ? "deposit" : "withdrawal";
        
        // Normalisiere Tier-Name für Display
        const tierKey = tierRaw.toLowerCase();
        const tierMap = {
          "dawn": "Dawn",
          "lightning": "Lightning",
          "flash": "Flash",
          "strength": "Strength",
          "thunder": "Thunder",
          "mjolnir": "Mjolnir",
          "vethor_x": "VethorX",
          "vethorx": "VethorX",
          "strength_x": "StrengthX",
          "strengthx": "StrengthX",
          "thunder_x": "ThunderX",
          "thunderx": "ThunderX",
          "mjolnir_x": "MjolnirX",
          "mjolnirx": "MjolnirX",
        };
        const tierDisplay = tierMap[tierKey] || tierRaw.charAt(0).toUpperCase() + tierRaw.slice(1).toLowerCase();
        
        return renderWithExchangeBadge(
          <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
            <img
              src="/Stargate.png"
              alt="Stargate"
              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
              draggable={false}
              {...RAW_TYPE_ICON_PROPS}
            />
            <div className="min-w-0 leading-tight">
              <div className="whitespace-normal break-words">NFT Stargate</div>
              <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                {tierDisplay} {typeDisplay}
              </div>
            </div>
          </div>
        );
      }
      
      // Fallback für alte Format oder unbekannte Klassifikationen
      const rest = classification.replace(/^NFT_Stargate_/, "");
      const restPretty = rest.replace(/_/g, " ");

      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Stargate.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">NFT Stargate</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              {restPretty}
            </div>
          </div>
        </div>
      );
    }

    if (["sale", "sale_payment"].includes(classification.toLowerCase())) {
      return renderWithExchangeBadge(
        renderNftType({
          title: "NFT Sale",
          tx,
          colorClass: "text-green-600",
          subtitleClass: "text-green-700/90",
        })
      );
    }

    if (classification.toLowerCase() === "stargate_staking_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Stargate.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Stargate</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Staking Rewards
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "stargate_boosted_maturity") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Stargate.png"
            alt="Stargate"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Stargate</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Boosted Maturity
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "thorhead_staking_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/thorhead.jpg"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Thorhead</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Staking Rewards
            </div>
          </div>
        </div>
      );
    }

    if (clsLower === "vemugs_staking_rewards") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/vemugs.png",
          alt: "VeMugs",
          title: "VeMugs",
          subtitle: "Staking Rewards",
        })
      );
    }

    if (clsLower === "vemugs_mint_reward") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/vemugs.png",
          alt: "VeMugs",
          title: "VeMugs",
          subtitle: "Mint Reward",
        })
      );
    }

    if (clsLower === "vemugs_reward_distribution") {
      const treasury =
        tx.vemugsRewardMode === "treasury_post_brew_reward" ||
        tx.subApplication === "Treasury Reward" ||
        tx.providerSuggestedLabel === "VeMugs Treasury Reward";
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/vemugs.png",
          alt: "VeMugs",
          title: "VeMugs",
          subtitle: treasury ? "Treasury Reward" : "Reward Distribution",
        })
      );
    }

    if (clsLower === "vemugs_mint_gas_refund") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 font-semibold min-w-0 text-pink-600">
          <img
            src={VEMUGS_ICON_SRC}
            alt="VeMugs"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5 rounded-sm"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">VeMugs</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-pink-700/90">
              Mint Gas Refund
            </div>
          </div>
        </div>
      );
    }

    if (clsLower === "vemugs_mint_zero_transfer") {
      const meta = getNftDisplayMeta(tx);
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 font-semibold min-w-0 text-pink-600">
          <img
            src={VEMUGS_ICON_SRC}
            alt="VeMugs"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5 rounded-sm"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">
              {meta.isBatch
                ? `VeMugs · Mint · Batch ${meta.batchSize}`
                : "VeMugs"}
            </div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-pink-700/90">
              {meta.isBatch && meta.batchPreview
                ? `0 VTHO Placeholder · ${meta.batchPreview}`
                : "Mint · 0 VTHO Placeholder"}
            </div>
          </div>
        </div>
      );
    }

    if (isWovStakingRewardsClass(classification)) {
      return renderWithExchangeBadge(renderWorldOfVTypeLabel("Staking Rewards"));
    }

    if (classification.toLowerCase() === "nft_creator_royalty") {
      return renderWithExchangeBadge(renderWorldOfVTypeLabel("Creator Royalty"));
    }

    if (isWovRestakeClass(classification)) {
      return renderWithExchangeBadge(renderWorldOfVTypeLabel("Restake"));
    }

    if (isWovClaimFeeClass(classification)) {
      return renderWithExchangeBadge(renderWorldOfVTypeLabel("Claim Fee"));
    }

    if (classification.toLowerCase() === "mva_staking_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/MVA.jpg"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">MVA</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Staking Rewards
            </div>
          </div>
        </div>
      );
    }

    const vaiyansDisplay = getVaiyansDisplay(clsLower);
    if (vaiyansDisplay) {
      return renderWithExchangeBadge(
        <VaiyansTypeLabel classification={clsLower} />
      );
    }

    if (clsLower === "one_million_pixel_purchase") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <Grid3X3
            className="h-5 w-5 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />

          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">
              1Million Pixel
            </div>

            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Pixel Purchase
            </div>
          </div>
        </div>
      );
    }

    const scoopUpDisplay = getScoopUpDisplay(clsLower);
    if (scoopUpDisplay) {
      return renderWithExchangeBadge(
        <ScoopUpTypeLabel classification={clsLower} />
      );
    }

    const veLotteryDisplay = getVeLotteryDisplay(clsLower);
    if (veLotteryDisplay) {
      return renderWithExchangeBadge(
        <VeLotteryTypeLabel classification={clsLower} />
      );
    }

    // 🐢 Squirtle Squad — Game spend / VTHO entry / reward / NFT upgrade
    if (
      clsLower === "squad_game_spend" ||
      clsLower === "squad_vtho_game_entry" ||
      clsLower === "squad_game_reward" ||
      clsLower === "squad_nft_upgrade_spend" ||
      clsLower === "squad_nft_upgrade_burn" ||
      clsLower === "squad_nft_upgrade_mint" ||
      clsLower === "squad_preico_payment" ||
      clsLower === "squad_preico_forward" ||
      clsLower === "squad_preico_claim"
    ) {
      const subtitle =
        clsLower === "squad_game_spend"
          ? language === "de"
            ? "Game Spend"
            : "Game Spend"
          : clsLower === "squad_vtho_game_entry"
            ? language === "de"
              ? "VTHO Game Entry"
              : "VTHO Game Entry"
          : clsLower === "squad_nft_upgrade_spend"
            ? language === "de"
              ? "NFT Upgrade Spend"
              : "NFT Upgrade Spend"
            : clsLower === "squad_nft_upgrade_burn"
              ? language === "de"
                ? "NFT Upgrade Burn"
                : "NFT Upgrade Burn"
              : clsLower === "squad_nft_upgrade_mint"
                ? language === "de"
                  ? "NFT Upgrade Mint"
                  : "NFT Upgrade Mint"
                : clsLower === "squad_preico_payment"
                  ? language === "de"
                    ? "Pre-ICO Pool"
                    : "Pre-ICO Pool"
                  : clsLower === "squad_preico_forward"
                    ? language === "de"
                      ? "Pre-ICO Pool Forward"
                      : "Pre-ICO Pool Forward"
                    : clsLower === "squad_preico_claim"
                      ? language === "de"
                        ? "Pre-ICO Claim"
                        : "Pre-ICO Claim"
                      : language === "de"
                        ? "Game Reward"
                        : "Game Reward";

      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/SQUAD.png"
            alt="Squirtle Squad"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Squirtle Squad</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              {subtitle}
            </div>
          </div>
        </div>
      );
    }

    // 🏰 Golden Empire (Genesis-Style: oben App-Name, unten Aktion — ohne Counterparty/IN/OUT)
    if (
      clsLower === "golden_empire_reward" ||
      clsLower === "golden_empire_spend" ||
      clsLower === "token_approval" ||
      clsLower.startsWith("golden_empire_") ||
      !!tx?.partOfGoldenEmpire
    ) {
      let actionLabel = "Interaction";
      if (clsLower === "golden_empire_reward") actionLabel = "Rewards";
      else if (clsLower === "golden_empire_spend") actionLabel = "Spend";
      else if (clsLower === "golden_empire_burn_spend") actionLabel = "Burn Spend";
      else if (clsLower === "token_approval") actionLabel = "Approval";
      else {
        const cleaned = String(classification || "").replace(/golden_empire_/i, "");
        const pretty = toTitleCase(cleaned);
        if (pretty) actionLabel = pretty;
      }

      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/GE.jpg"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Golden Empire</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              {actionLabel}
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "verocket_generated_vtho_claim") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/VeRocket.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">VeRocket</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              VTHO Claim
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "vexchange_farm_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Vexchange.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Vexchange</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Farm Rewards
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "betterswap_pool2earn_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/Betterswap.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Betterswap</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Pool2earn Rewards
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "listen2earn_rewards") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/nfbc.png"
            alt="NFBC"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">NFBC</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Listen2earn Rewards
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "wanchain_bridge_to_earn_reward") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/WAN.png",
          alt: "Wanchain",
          title: "Wanchain Bridge-to-Earn Reward",
          subtitle: language === "de" ? "Cross-Chain Task Reward" : "Cross-Chain Task Reward",
        })
      );
    }

    if (classification.toLowerCase() === "bangzboardz_staking_reward") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">BangzBoardz Staking Reward</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Paladins / NFT Staking
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "goatz_milk_nft_holder_reward") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/goatz.png",
          alt: "Goatz Club",
          title: "Goatz Club",
          subtitle: language === "de" ? "MILK NFT Holder Reward" : "MILK NFT Holder Reward",
        })
      );
    }

    const b3moDisplay = getB3moDisplay(classification, tx);
    if (b3moDisplay) {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: B3MO_ICON_SRC,
          iconClass: B3MO_ICON_CLASS,
          alt: "B3MO",
          title: b3moDisplay.title,
          subtitle: b3moDisplay.subtitle,
        })
      );
    }

    const vaultxDisplay = getVaultXDisplay(classification);
    if (vaultxDisplay) {
      return renderWithExchangeBadge(
        <VaultXTypeLabel classification={classification} />
      );
    }

    if (
      clsLower === "airdrop" ||
      clsLower === "x2earn_reward" ||
      clsLower === "x2earn_reward_claim" ||
      clsLower === "x2earn_reward_distribution"
    ) {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">X2Earn Reward</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
              Claim
            </div>
          </div>
        </div>
      );
    }

    if (clsLower === "safehaven_staking_rewards") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/sha.png",
          alt: "Safehaven",
          title: "Safehaven",
          subtitle: "Staking Rewards",
        })
      );
    }

    if (clsLower === "sagaz_reward_claim") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/sagaz.jpg",
          alt: "Sagaz",
          title: "Sagaz",
          subtitle: "Reward Claim",
        })
      );
    }

    if (clsLower === "vfox_nft_holder_reward") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VFA.jpg",
          alt: "VFA",
          title: "VFA",
          subtitle: "NFT Holder Reward",
        })
      );
    }

    if (clsLower === "vesea_nft_staking_rewards") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VSEA.png",
          alt: "VeSea",
          title: "VeSea",
          subtitle: "NFT Staking Rewards",
        })
      );
    }

    if (clsLower === "union_nft_holder_rewards") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/UNION.png",
          alt: "UNION",
          title: "UNION",
          subtitle: "NFT Holder Rewards",
        })
      );
    }

    if (clsLower === "vimworld_veed_reward_distribution") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VIM.png",
          alt: "VIMworld",
          title: "VIMworld",
          subtitle: "Reward Distribution",
        })
      );
    }

    if (clsLower === "vimworld_veed_deposit") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VIM.png",
          alt: "VIMworld",
          title: "VIMworld",
          subtitle: "VEED Deposit",
        })
      );
    }

    if (clsLower === "vimworld_veed_withdrawal") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VIM.png",
          alt: "VIMworld",
          title: "VIMworld",
          subtitle: "VEED Withdrawal",
        })
      );
    }

    if (clsLower === "vimworld_veed_feed_fee") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VIM.png",
          alt: "VIMworld",
          title: "VIMworld",
          subtitle: "VEED Feed Fee",
        })
      );
    }

    if (clsLower === "vimworld_veed_withdrawal_fee") {
      return renderWithExchangeBadge(
        renderBlueAppType({
          icon: "/VIM.png",
          alt: "VIMworld",
          title: "VIMworld",
          subtitle: "VEED Withdrawal Fee",
        })
      );
    }

    if (classification.toLowerCase() === "governance_reward" || classification === "Governance_reward") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Governance Voting</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
              Reward
            </div>
          </div>
        </div>
      );
    }

    // Solarwise Transaktionen
    if (classification.toLowerCase() === "solarwise_electricity_revenue") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/solarwise.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Solarwise</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Electricity Revenue
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "solarwise_stake_deposit") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/solarwise.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Solarwise</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Stake Deposit
            </div>
          </div>
        </div>
      );
    }

    if (classification.toLowerCase() === "solarwise_stake_withdrawal") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/solarwise.png"
            alt=""
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Solarwise</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              Stake Withdrawal
            </div>
          </div>
        </div>
      );
    }

    // Andere Solarwise-Transaktionen (NFT, Certificate, etc.) bleiben wie bisher
    if (classification.toLowerCase().startsWith("solarwise_")) {
      const clsLower = classification.toLowerCase();
      let label = classification;
      if (clsLower === "solarwise_nft_stake_deposit") {
        label = language === "de" ? "Solarwise NFT Stake Deposit" : "Solarwise NFT Stake Deposit";
      } else if (clsLower === "solarwise_nft_stake_withdrawal") {
        label = language === "de" ? "Solarwise NFT Stake Withdrawal" : "Solarwise NFT Stake Withdrawal";
      } else if (clsLower === "solarwise_certificate_mint") {
        label = language === "de" ? "Solarwise Certificate Mint" : "Solarwise Certificate Mint";
      } else if (clsLower === "solarwise_certificate_burn") {
        label = language === "de" ? "Solarwise Certificate Burn" : "Solarwise Certificate Burn";
      } else {
        label = classification.replace("solarwise_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      }
      return renderWithExchangeBadge(
        <span className="text-blue-600 font-semibold">
          ☀️ {label}
        </span>
      );
    }

    // Internal Transfer (2-line): oben Transfer, unten Internal
    {
      const clsLower = String(classification || "").toLowerCase();
      const isInternal =
        clsLower === "internal_transfer" ||
        clsLower.startsWith("internal_transfer") ||
        clsLower === "internal" ||
        !!tx.internal_flag;

      if (isInternal) {
        return renderWithExchangeBadge(
          <div className="flex items-start gap-1.5 text-gray-900 font-normal min-w-0">
            <span className="text-sm flex-shrink-0 mt-0.5 leading-none">↔️</span>
            <div className="min-w-0 leading-tight">
              <div className="whitespace-normal break-words">Transfer</div>
              <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                Internal
              </div>
            </div>
          </div>
        );
      }
    }

    // Refund (2-line): oben Transfer, unten Refund
    {
      const clsLower = String(classification || "").toLowerCase();
      const isRefund =
        clsLower === "refund" ||
        clsLower.startsWith("refund") ||
        !!tx.refund ||
        !!tx.refundOfHash ||
        !!tx.refund_of_hash;

      if (isRefund) {
        return renderWithExchangeBadge(
          <div className="flex items-start gap-1.5 text-gray-900 font-normal min-w-0">
            <span className="text-sm flex-shrink-0 mt-0.5 leading-none">↩️</span>
            <div className="min-w-0 leading-tight">
              <div className="whitespace-normal break-words">Transfer</div>
              <div className="whitespace-normal break-words text-[11px] font-medium text-emerald-700/90">
                Refund
              </div>
            </div>
          </div>
        );
      }
    }

    // NFT Mints (inkl. Galaxy)
    const cLower = String(classification || "").toLowerCase();

    if (cLower === "node_legacy_rewards") {
      return renderWithExchangeBadge(<span className="text-gray-800">Legacy Node Rewards</span>);
    }

    if (cLower === "foundation_bonus") {
      return renderWithExchangeBadge(<span className="text-gray-800">Foundation Bonus/Swap</span>);
    }

    // 💧 Liquidity Add/Remove (inkl. LP In/Out sichtbar lassen)
    if (cLower.startsWith("liquidity_")) {
      // Farben an Vip180TokenTable angelehnt:
      // - underlying/position OUT bzw. lp_out → orange
      // - underlying/position IN bzw. lp_in → grün
      // - burn → rot
      const isBurn = cLower.includes("burn");
      const isOut =
        cLower.includes("underlying_out") ||
        cLower.includes("position_out") ||
        cLower.includes("lp_out") ||
        cLower.endsWith("_out");
      const isIn =
        cLower.includes("underlying_in") ||
        cLower.includes("position_in") ||
        cLower.includes("lp_in") ||
        cLower.endsWith("_in");

      const colorClass = isBurn
        ? "text-red-600"
        : isIn
          ? "text-green-600"
          : isOut
            ? "text-orange-600"
            : "text-indigo-600";

      // Extrahiere den spezifischen Typ (z.B. "add_lp_in" → "Add LP In")
      let typeLabel = classification.replace(/^liquidity_/i, ""); // Entferne "liquidity_" Präfix
      if (typeLabel) {
        // Konvertiere zu Title Case und ersetze Unterstriche durch Leerzeichen
        typeLabel = typeLabel
          .split("_")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
      } else {
        typeLabel = "Transaction";
      }

      const txidLower = String(
        tx?.hash ||
          tx?.txid ||
          tx?.transactionHash ||
          tx?.event_id ||
          ""
      ).toLowerCase();

      const liquidityFamilyKey = liquidityFamilyContextKey(tx, classification);

      let dex =
        guessDexFromTx(tx) ||
        (liquidityFamilyKey
          ? dexByLiquidityFamily.get(liquidityFamilyKey)
          : dexByTxid.get(txidLower));

      if (!dex) {
        const lpSym =
          (liquidityFamilyKey
            ? lpSymbolByLiquidityFamily.get(liquidityFamilyKey)
            : lpSymbolByTxid.get(txidLower)) ||
          (isLpPositionToken(String(tx?.token || ""))
            ? tx?.token_symbol || tx?.tokenSymbol || ""
            : "");

        const pool =
          extractPoolFromLpToken(String(tx?.token || ""), tx) ||
          tx?.liquidityPairAddress ||
          tx?.poolAddress ||
          tx?.pairAddress ||
          tx?.pool ||
          tx?.pair ||
          tx?.contract ||
          tx?.token_contract ||
          tx?.tokenContract ||
          null;

        dex = getDexInfoFromLpSymbol(lpSym, pool);
      }
      const title = dex?.name || "Liquidity";
      const subtitle = dex?.name ? `Liquidity · ${typeLabel}` : typeLabel;

      return renderWithExchangeBadge(
        <div className={`flex items-start gap-1.5 ${colorClass} font-semibold min-w-0`}>
          {dex?.icon ? (
            <img
              src={dex.icon}
              alt={dex.name}
              className={getLiquidityDexIconClassName(dex)}
              draggable={false}
              {...RAW_TYPE_ICON_PROPS}
              onError={(e) => {
                console.warn("[RawTransfersTable] Liquidity icon missing:", dex.icon);
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className="text-sm flex-shrink-0 mt-0.5 leading-none">💧</span>
          )}
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">{title}</div>
            <div className={`whitespace-normal break-words text-[11px] font-medium ${colorClass}/90`}>
              {subtitle}
            </div>
          </div>
        </div>
      );
    }
    if (cLower === "nft_galaxy_mint" || classification === "NFT_Galaxy_mint") {
      // Galaxy NFT Tier basierend auf Betrag oder nftName bestimmen
      const amount = Number(tx.amount || tx.amount_display || tx.value || 0);
      const nftName = tx.nftName || tx.nft_name || "";

      const getGalaxyTier = (amt, name) => {
        // Versuche zuerst aus nftName zu extrahieren
        if (name) {
          const nameLower = name.toLowerCase();
          if (nameLower.includes("moon")) return "Moon";
          if (nameLower.includes("mercury")) return "Mercury";
          if (nameLower.includes("venus")) return "Venus";
          if (nameLower.includes("mars")) return "Mars";
          if (nameLower.includes("jupiter")) return "Jupiter";
          if (nameLower.includes("saturn")) return "Saturn";
          if (nameLower.includes("uranus")) return "Uranus";
          if (nameLower.includes("neptune")) return "Neptune";
          if (
            nameLower.includes("galaxy") &&
            !nameLower.includes("moon") &&
            !nameLower.includes("mercury") &&
            !nameLower.includes("venus") &&
            !nameLower.includes("mars") &&
            !nameLower.includes("jupiter") &&
            !nameLower.includes("saturn") &&
            !nameLower.includes("uranus") &&
            !nameLower.includes("neptune")
          ) {
            return "Galaxy";
          }
        }

        // Fallback: Bestimme aus Betrag
        if (amt === 5000 || amt === 10000) return "Moon";
        if (amt === 7500 || amt === 12500) return "Mercury";
        if (amt === 25000) return "Venus";
        if (amt === 50000) return "Mars";
        if (amt === 125000) return "Jupiter";
        if (amt === 250000) return "Saturn";
        if (amt === 1250000) return "Uranus";
        if (amt === 2500000) return "Neptune";
        if (amt === 12500000) return "Galaxy";
        return "Galaxy"; // Fallback
      };

      const tierName = getGalaxyTier(amount, nftName);

      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-pink-600 font-semibold min-w-0">
          <img
            src="/nft-icon.svg"
            alt="NFT Galaxy"
            className="w-5 h-5"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">NFT Galaxy</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-pink-700/90">
              Upgrade {tierName}
            </div>
          </div>
        </div>
      );
    }

    if (cLower === "nft_mint" || cLower === "mint_payment") {
      // Same pattern as Thorheads: pink NFT Mint row + collection icon.
      if (isVeMugsNft(tx)) {
        return renderWithExchangeBadge(
          renderNftType({
            title: "NFT Mint",
            tx,
            colorClass: "text-pink-600",
            subtitleClass: "text-pink-700/90",
            iconSrc: VEMUGS_ICON_SRC,
            alt: "VeMugs",
            brand: "",
          })
        );
      }

      return renderWithExchangeBadge(
        renderNftType({
          title: "NFT Mint",
          tx,
          colorClass: "text-pink-600",
          subtitleClass: "text-pink-700/90",
          brand: "",
        })
      );
    }

    if (cLower === "nft_marketplace_purchase_payment") {
      return renderWithExchangeBadge(
        renderNftType({
          title: language === "en" ? "NFT Purchase" : "NFT-Kauf",
          tx,
          colorClass: "text-pink-600",
          subtitleClass: "text-pink-700/90",
        })
      );
    }

    // Swap (2-line): oben "Swap", unten Richtung + IN/OUT Pill (Multi-Swaps per TXID)
    {
      const cLower = String(classification || "").toLowerCase();
      const txidLower = String(tx?.hash || "").toLowerCase();
      const isOut = cLower === "swap_out" || cLower.includes("swap_out");
      const isIn = cLower === "swap_in" || cLower.includes("swap_in");
      const isRefund = cLower === "swap_refund_in" || cLower.includes("swap_refund_in");

      const rowToken = String(
        tx?.token ||
        tx?.token_symbol ||
        tx?.tokenSymbol ||
        ""
      )
        .trim()
        .toUpperCase();

      const rowCounterToken = String(
        tx?.swap_counter_asset ||
        tx?.swapCounterAsset ||
        ""
      )
        .trim()
        .toUpperCase();

      const directPairLabel =
        rowToken && rowCounterToken
          ? isOut
            ? `${rowToken} → ${rowCounterToken}`
            : (isIn || isRefund)
              ? `${rowCounterToken} → ${rowToken}`
              : ""
          : "";

      const explicitPairId = String(
        tx?.swap_pair_id ||
        tx?.swapPairId ||
        ""
      ).trim();

      const clauseKey = swapClauseContextKey(tx);
      const lookupKeys = [
        explicitPairId ? `pair:${explicitPairId}` : "",
        clauseKey,
        txidLower ? `hash:${txidLower}` : "",
      ].filter(Boolean);

      let pair = null;
      for (const key of lookupKeys) {
        const candidate = swapPairByTxid.get(key);
        if (candidate) {
          pair = candidate;
          break;
        }
      }

      const fallbackPairLabel =
        pair
          ? `${pair.from} → ${pair.to}`
          : "";

      const pairLabel =
        directPairLabel ||
        fallbackPairLabel;

      const pill = (text, variant = "green") => {
        const base =
          "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide ring-1";
        if (variant === "orange")
          return <span className={`${base} bg-orange-50 text-orange-700 ring-orange-200/70`}>{text}</span>;
        if (variant === "blue")
          return <span className={`${base} bg-blue-50 text-blue-700 ring-blue-200/70`}>{text}</span>;
        if (variant === "green-outline")
          return <span className={`${base} bg-white text-emerald-700 ring-emerald-200/80`}>{text}</span>;
        return <span className={`${base} bg-emerald-50 text-emerald-700 ring-emerald-200/70`}>{text}</span>;
      };

      if (isOut) {
        return renderWithExchangeBadge(
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-orange-600 font-semibold inline-flex items-center gap-1">
                🔄 <span>Swap</span>
              </span>
              {pill("OUT", "orange")}
            </div>
            {pairLabel ? (
              <div className="whitespace-normal break-words text-[11px] font-medium text-orange-700/80">
                {pairLabel}
              </div>
            ) : null}
          </div>
        );
      }

      if (isIn) {
        return renderWithExchangeBadge(
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-green-600 font-semibold inline-flex items-center gap-1">
                🔄 <span>Swap</span>
              </span>
              {pill("IN", "green")}
            </div>
            {pairLabel ? (
              <div className="whitespace-normal break-words text-[11px] font-medium text-emerald-700/80">
                {pairLabel}
              </div>
            ) : null}
          </div>
        );
      }

      if (isRefund) {
        return renderWithExchangeBadge(
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-green-600 font-semibold inline-flex items-center gap-1">
                🔄 <span>Swap</span>
              </span>
              {pill("IN", "green")}
              {pill("REFUND", "green-outline")}
            </div>
            {pairLabel ? (
              <div className="whitespace-normal break-words text-[11px] font-medium text-emerald-700/80">
                {pairLabel}
              </div>
            ) : (
              <div className="text-[11px] font-medium text-emerald-700/80">Refund</div>
            )}
          </div>
        );
      }

      if (cLower === "gas_abstraction_fee") {
        return renderWithExchangeBadge(
          <GasAbstractionFeeLabel
            language={language}
            token={tx?.token || tx?.token_symbol || tx?.tokenSymbol || tx?.symbol || tx?.asset || ""}
          />
        );
      }

      if (cLower === "swap_proceeds_sent") {
        return renderWithExchangeBadge(
          TwoLine({
            title: "Swap",
            subtitle: language === "en" ? "Proceeds spent" : "Ergebnis verwendet",
            icon: "🔄",
            colorClass: "text-blue-600",
            subtitleClass: "text-blue-700/90",
            bold: false,
          })
        );
      }
    }

    // Wrap/Unwrap (UI): oben "Wrap"/"Unwrap", unten Richtung + IN/OUT Pill (VVET/VET etc. inklusive)
    {
      const m = String(classification || "").match(/^(.*)_(wrap|unwrap)$/i);
      if (m && !isVetDomainsClass(classification)) {
        const action = String(m[2] || "").toLowerCase();
        const parts = String(m[1] || "").split("_").filter(Boolean);

        if (parts.length >= 2) {
          const from = parts[0].toUpperCase();
          const to = parts.slice(1).join("_").toUpperCase();
          const title = action === "wrap" ? "Wrap" : "Unwrap";
          const direction = `${from} → ${to}`;

          const tokenHere = String(tx.token || tx.token_symbol || tx.tokenSymbol || "")
            .trim()
            .toUpperCase();

          const pillLabel = tokenHere === from ? "OUT" : tokenHere === to ? "IN" : null;

          return renderWithExchangeBadge(
            TwoLine({
              title,
              subtitle: direction,
              colorClass: "text-gray-900",
              subtitleClass: "text-gray-700/90",
              bold: false,
              pillLabel,
              pillKind: pillLabel,
            })
          );
        }
      }
    }


    // Exchange Deposits & Withdrawals - Stargate-Style Formatierung
    if (classification === "exchange_deposit" || classification === "exchange_withdrawal") {
      const isDeposit = classification === "exchange_deposit";
      const effectiveExchangeName = exchangeName || tx.exchange_source || tx.exchange || "";
      const exchangeDisplay = effectiveExchangeName
        ? effectiveExchangeName.charAt(0).toUpperCase() + effectiveExchangeName.slice(1).toLowerCase()
        : "";
      const actionLabel = isDeposit ? "Deposit" : "Withdrawal";
      
      return (
        <div className="flex items-start gap-1.5 text-gray-900 font-normal min-w-0">
          <span className="text-sm flex-shrink-0 mt-0.5 leading-none">↔️</span>
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">Exchange</div>
            <div className="whitespace-normal break-words text-[11px] font-normal text-gray-700/90">
              {exchangeDisplay ? `${actionLabel} ${exchangeDisplay}` : actionLabel}
            </div>
          </div>
        </div>
      );
    }
    if (classification === "exchange_trade_buy") {
      return renderWithExchangeBadge(<span className="text-green-600 font-semibold">Exchange Buy</span>);
    }
    if (classification === "exchange_trade_sell") {
      return renderWithExchangeBadge(<span className="text-red-600 font-semibold">Exchange Sell</span>);
    }
    if (classification === "exchange_fee") {
      return renderWithExchangeBadge(<span className="text-orange-600 font-semibold">Exchange Fee</span>);
    }

    if (classification === "mugshot_pro_subscription") {
      return renderWithExchangeBadge(
        <MugshotProSubscriptionTypeLabel tx={tx} language={language} />
      );
    }

    if (classification === "vewin_4ocean_donation") {
      return renderWithExchangeBadge(
        <FourOceanDonationTypeLabel language={language} />
      );
    }

    if (classification === "v3tax_purchase") {
      const planSub = formatV3TaxPurchaseSubline(tx, language);
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
          <img
            src="/logo copy.png"
            alt="V3Tax"
            className="h-5 w-5 object-cover flex-shrink-0 mt-0.5 rounded-[22%] ring-1 ring-slate-200/70"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">V3Tax</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
              {planSub}
            </div>
          </div>
        </div>
      );
    }

    if (classification === "nfbc_purchase") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
          <img
            src="/nfbc.png"
            alt="NFBC"
            className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
            draggable={false}
            {...RAW_TYPE_ICON_PROPS}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">NFBC</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
              {language === "de" ? "Kauf" : "Purchase"}
            </div>
          </div>
        </div>
      );
    }

    if (classification === "received") {
      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">{t("received", language)}</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
              Unclassified
            </div>
          </div>
        </div>
      );
    }
    if (classification === "sent") {
      // ✅ SolarWise Sun Ticket fallback: even if backend still marks it as sent,
      // render it in SolarWise style when the recipient is the SunTicket contract.
      const toLower = String(tx?.to || tx?.recipient || tx?.receiver || "").toLowerCase();
      const tokenUpper = String(
        tx?.token || tx?.token_symbol || tx?.tokenSymbol || tx?.symbol || tx?.asset || ""
      )
        .trim()
        .toUpperCase();

      if (toLower === SOLARWISE_SUNTICKET_CONTRACT && tokenUpper === "VET") {
        return renderWithExchangeBadge(
          <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
            <img
              src="/solarwise.png"
              alt=""
              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
              draggable={false}
              {...RAW_TYPE_ICON_PROPS}
            />
            <div className="min-w-0 leading-tight">
              <div className="whitespace-normal break-words">Solarwise</div>
              <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                Sun Ticket
              </div>
            </div>
          </div>
        );
      }

      return renderWithExchangeBadge(
        <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
          <div className="min-w-0 leading-tight">
            <div className="whitespace-normal break-words">{t("sent", language)}</div>
            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
              Unclassified
            </div>
          </div>
        </div>
      );
    }

    if (isJuicyFinanceClass(classification)) {
      return renderWithExchangeBadge(
        renderJuicyFinanceType(
          classification,
          tx?.token || tx?.token_symbol || tx?.tokenSymbol || tx?.symbol || tx?.asset || ""
        )
      );
    }

    // Generic 2-line fallback for underscore classifications (harmonisiert UI)
    if (classification && classification.includes("_") && !isVetDomainsClass(classification)) {
      const wovAuctionBidFallback = renderWovAuctionBidTypeCell(
        classification,
        language,
        renderWithExchangeBadge
      );
      if (wovAuctionBidFallback) return wovAuctionBidFallback;

      const parts = classification.split("_").filter(Boolean);
      if (parts.length >= 2) {
        const title = toTitleCase(parts[0]);
        const subtitle = toTitleCase(parts.slice(1).join(" "));
        return renderWithExchangeBadge(
          <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
            <div className="min-w-0 leading-tight">
              <div className="whitespace-normal break-words">{title}</div>
              <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                {subtitle}
              </div>
            </div>
          </div>
        );
      }
    }

    return renderWithExchangeBadge(
      <span>{classification ? toTitleCase(classification) : ""}</span>
    );
  }

  // PDF: filtered/pdfRawChunks sind useMemo — kein useEffect-Race beim Drucken
  const RAW_PDF_ROWS_PER_PAGE = 34;

  const pdfRawChunks = useMemo(() => {
    if (!pdfExportMode) return [filtered];

    const chunks = [];
    for (let i = 0; i < filtered.length; i += RAW_PDF_ROWS_PER_PAGE) {
      chunks.push(filtered.slice(i, i + RAW_PDF_ROWS_PER_PAGE));
    }
    return chunks;
  }, [filtered, pdfExportMode]);

  const renderRawTransfersThead = () => (
    <thead className={cn(T.thead, primaryTableHeadClass)}>
      <tr>
        <th className={cn(T.th, "text-left", pdfExportMode ? "pdf-wrap w-[16%]" : isEditMode ? "raw-type-col-edit w-[14%] max-w-0" : "w-[14%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
          </span>
        </th>

        {!pdfExportMode && (
          <th className={cn(T.th, "text-center", "w-[7%]")}>
            <span className={T.thInnerCenter}>
              <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("source", language)}</span>
            </span>
          </th>
        )}

        <th className={cn(T.th, "text-center", pdfExportMode ? "w-[7%]" : "w-[7%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
          </span>
        </th>

        <th className={cn(T.th, "text-center", pdfExportMode ? "w-[10%]" : "w-[9%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
          </span>
        </th>

        <th className={cn(T.th, "text-center", pdfExportMode ? "w-[16%]" : "w-[14%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("recipient", language)}</span>
          </span>
        </th>

        <th className={cn(T.th, "text-center", pdfExportMode ? "w-[16%]" : "w-[14%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("sender", language)}</span>
          </span>
        </th>

        <th className={cn(T.th, "text-center", pdfExportMode ? "w-[23%]" : "w-[20%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
          </span>
        </th>

        <th className={cn("raw-date-col", T.th, "text-center", pdfExportMode ? "w-[12%]" : "w-[11%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
          </span>
        </th>
      </tr>
    </thead>
  );

  const renderRawTransfersRow = (tx, index) => {
    const key = `${tx.__source}-${tx.hash}-${tx.token}-${tx.timestamp}-${tx.type}-${index}`;

    return (
      <tr
        key={key}
        className={T.tr}
        data-index={!pdfExportMode ? index : undefined}
        ref={!pdfExportMode ? rowVirtualizer.measureElement : undefined}
      >
        <td
          className={cn(
            "py-4",
            pdfExportMode
              ? "px-6 pdf-wrap break-words text-[11px] leading-tight"
              : isEditMode
                ? "raw-type-col-edit px-3 max-w-0 min-w-0 overflow-hidden"
                : "px-6 break-words"
          )}
        >
          {isEditMode && !pdfExportMode ? (
            <select
              className="w-full max-w-full min-w-0 border border-gray-300 rounded px-2 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 truncate"
              value={getPickerClassification(tx)}
              title={getPickerClassification(tx)}
              onChange={(e) => {
                const nextValue = e.target.value;
                const validation = validateManualOverrideClassificationForUi(nextValue);
                if (!validation.ok) {
                  toast.error(language === "en" ? validation.messageEn : validation.message);
                  return;
                }
                const override = buildManualOverridePayload(tx, nextValue);
                onClassificationChange(override, nextValue, tx);
                onEditDetected();
              }}
            >
              <optgroup label={manualOverrideSelectGroups.groupLabels.tier1}>
                {manualOverrideSelectGroups.tier1.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>

              <optgroup label={manualOverrideSelectGroups.groupLabels.tierWrap}>
                {manualOverrideSelectGroups.tierWrap.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>

              <optgroup label={manualOverrideSelectGroups.groupLabels.tier2Static}>
                {manualOverrideSelectGroups.tier2Static.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>

              {manualOverrideSelectGroups.tier2Dynamic.length > 0 && (
                <optgroup label={manualOverrideSelectGroups.groupLabels.tier2Dynamic}>
                  {manualOverrideSelectGroups.tier2Dynamic.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              )}

              {!manualOverrideOptionValues.has(getPickerClassification(tx)) &&
                getPickerClassification(tx) !== "auto" && (
                  <option
                    value={getPickerClassification(tx)}
                    disabled={isManualOverrideClassBlocked(getPickerClassification(tx))}
                  >
                    {getPickerClassification(tx)}
                    {isManualOverrideClassBlocked(getPickerClassification(tx))
                      ? language === "en"
                        ? " (auto)"
                        : " (auto)"
                      : ""}
                  </option>
                )}
            </select>
          ) : (
            renderTypeCell(tx)
          )}
        </td>

        {!pdfExportMode && (
          <td className="px-4 py-4 text-center">
            {displayTransferSource(tx) === "VETVTHO" ? (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-sky-50 text-sky-800 border border-sky-200 ring-1 ring-sky-100/60">
                {displayTransferSource(tx)}
              </span>
            ) : displayTransferSource(tx) === "VIP180" ? (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 ring-1 ring-emerald-100/60">
                {displayTransferSource(tx)}
              </span>
            ) : displayTransferSource(tx) === "THOR LOG" ? (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-violet-50 text-violet-800 border border-violet-200 ring-1 ring-violet-100/60"
                title="Thor Protocol Event (nicht VeChainStats VIP180)"
              >
                THOR LOG
              </span>
            ) : displayTransferSource(tx) === "RECEIPT DERIVED" ? (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 ring-1 ring-indigo-100/60"
                title="Aus receipt-verifizierten Wallet- und Router-Flows abgeleitet"
              >
                RECEIPT DERIVED
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-slate-900/5 px-2 py-0.5 text-xs font-semibold text-slate-700">
                {displayTransferSource(tx)}
              </span>
            )}
          </td>
        )}

        <td className="px-4 py-4 text-center">{renderTokenCell(tx, pdfExportMode)}</td>

        <td
          className="px-4 py-4 font-mono text-gray-900 print:text-black text-center"
          title={resolveTokenAmountTooltip(tx) || undefined}
        >
          {formatTxTokenAmount(tx, 6)}
        </td>

        <td className="px-4 py-4 font-mono text-gray-600 text-center">
          {pdfExportMode ? (
            <span className="font-mono text-[11px] text-black pdf-break-all">
              {tx.receiver || "-"}
            </span>
          ) : tx.receiver ? (
            <button
              type="button"
              className="font-mono text-gray-600 hover:text-gray-800 hover:underline underline-offset-2 cursor-copy"
              title={tx.receiver}
              onClick={(e) => {
                e.stopPropagation();
                copyAddressWithToast(tx.receiver);
              }}
            >
              {shortAddr(tx.receiver)}
            </button>
          ) : (
            "-"
          )}
        </td>

        <td className="px-4 py-4 font-mono text-gray-600 text-center">
          {pdfExportMode ? (
            <span className="font-mono text-[11px] text-black pdf-break-all">
              {tx.sender || "-"}
            </span>
          ) : tx.sender ? (
            <button
              type="button"
              className="font-mono text-gray-600 hover:text-gray-800 hover:underline underline-offset-2 cursor-copy"
              title={tx.sender}
              onClick={(e) => {
                e.stopPropagation();
                copyAddressWithToast(tx.sender);
              }}
            >
              {shortAddr(tx.sender)}
            </button>
          ) : (
            "-"
          )}
        </td>

        <td className="px-4 py-4 font-mono text-gray-600 text-center">
          {tx.hash ? (
            pdfExportMode ? (
              <span className="font-mono text-[11px] text-black pdf-break-all">
                {tx.hash}
              </span>
            ) : (
              <button
                type="button"
                title={tx.hash}
                onClick={(e) => {
                  e.stopPropagation();
                  copyHashWithToast(tx.hash);
                }}
                className="font-mono text-gray-600 hover:text-gray-800 hover:underline underline-offset-2 cursor-copy"
              >
                {shortHash(tx.hash)}
              </button>
            )
          ) : (
            "-"
          )}
        </td>

        <td className="raw-date-col px-4 py-4 text-gray-500 text-center">
          {pdfExportMode ? (
            <span className="font-mono text-[11px] text-black whitespace-nowrap">
              {tx.timestamp
                ? (pdfExportMode
                    ? formatDateTimeUtcFromTimestamp(tx.timestamp)
                    : formatDateFromTimestamp(tx.timestamp))
                : "-"}
            </span>
          ) : (
            tx.timestamp
              ? (pdfExportMode
                  ? formatDateTimeUtcFromTimestamp(tx.timestamp)
                  : formatDateFromTimestamp(tx.timestamp))
              : "-"
          )}
        </td>
      </tr>
    );
  };

  return (
    // NOTE (PDF): do NOT add an extra page-break wrapper here.
    // Page breaks are controlled by the surrounding <section className="report-section ...">.
    <section
      className={cn(
        T.shell,
        pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
      )}
    >
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {language === "de" ? "Klassifiziertes Ledger" : "Classified ledger"}
            </span>

            <h2 className={T.title}>
              {language === "de"
                ? "Transfers & Protokoll-Events"
                : "Transfers & protocol events"}
            </h2>
            <p className={cn(T.subtitle, "whitespace-nowrap overflow-hidden text-ellipsis")}>
              {classifiedLedgerSubtitle}
            </p>
          </div>

          {!pdfExportMode ? (
            <div className={cn(T.headerAsidePills, "items-center flex-nowrap lg:max-w-none")}>
              {canToggleEditMode && (
                <button
                  type="button"
                  disabled={isPreviewMode}
                  aria-pressed={isEditMode}
                  onClick={onToggleEditMode}
                  title={
                    isPreviewMode
                      ? language === "de"
                        ? "Nach Kauf verfügbar"
                        : "Available after purchase"
                      : isEditMode
                        ? language === "de"
                          ? "Bearbeitungsmodus beenden"
                          : "Exit edit mode"
                        : language === "de"
                          ? "Manuelle Klassifizierungen bearbeiten"
                          : "Edit manual classifications"
                  }
                  className={cn(
                    "inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-semibold tracking-[0.06em] shadow-sm transition-all duration-200",
                    isPreviewMode &&
                      "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 opacity-60",
                    !isPreviewMode &&
                      isEditMode &&
                      "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-950 ring-2 ring-amber-200/70 hover:from-amber-100 hover:to-orange-100",
                    !isPreviewMode &&
                      !isEditMode &&
                      "border-sky-200 bg-white text-sky-900 hover:border-sky-300 hover:bg-sky-50/90"
                  )}
                >
                  {isPreviewMode ? (
                    <Lock className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                  ) : isEditMode ? (
                    <X className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                  ) : (
                    <Pencil className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                  )}
                  <span className="whitespace-nowrap">
                    {isPreviewMode
                      ? language === "de"
                        ? "Bearbeitung gesperrt"
                        : "Editing locked"
                      : isEditMode
                        ? language === "de"
                          ? "Bearbeitung beenden"
                          : "Exit edit mode"
                        : language === "de"
                          ? "Bearbeitungsmodus"
                          : "Edit mode"}
                  </span>
                  {!isPreviewMode && pendingOverrideCount > 0 && (
                    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                      {pendingOverrideCount}
                    </span>
                  )}
                  {!isPreviewMode && !isEditMode && savedOverrideCount > 0 && (
                    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold leading-none text-sky-800">
                      {savedOverrideCount}
                    </span>
                  )}
                </button>
              )}

              <span
                className={cn(
                  T.headerChipSoft,
                  "whitespace-nowrap text-sky-700 border-sky-200 bg-sky-50/80"
                )}
              >
                <span className="h-2 w-2 rounded-full bg-sky-500 shrink-0" />
                <span>VET / VTHO</span>
              </span>

              <span className={cn(T.headerChipSoft, "whitespace-nowrap text-emerald-800 border-emerald-200 bg-emerald-50/80")}>
                <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                <span>VIP-180</span>
              </span>
            </div>
          ) : (
            <div className={cn(T.headerAside, "text-sm text-slate-700")}>
              {filtered.length > 0 ? `${filtered.length} ${t("transactions", language)}` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      {!pdfExportMode && isEditMode && (
        <div className="border-b border-amber-200/80 bg-gradient-to-r from-amber-50/95 via-orange-50/70 to-amber-50/95 px-5 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2 text-sm text-amber-950">
            <span className="inline-flex h-2 w-2 rounded-full bg-amber-500 animate-pulse" aria-hidden />
            <span className="font-semibold">
              {language === "de" ? "Manueller Bearbeitungsmodus aktiv" : "Manual edit mode active"}
            </span>
            <span className="text-amber-800/90">
              {language === "de"
                ? "Klassifizierungen in der Typ-Spalte anpassen — speichern über die Leiste unten."
                : "Adjust classifications in the Type column — save via the bar below."}
            </span>
          </div>
        </div>
      )}

      {!pdfExportMode && (
        <div className={cn(T.toolbarWrap, "print:hidden")}>
          <div className={cn(T.toolbar)}>
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="min-w-0">
                {hasSelectedYear ? (
                  <>
                    <label
                      className={`inline-flex items-center gap-3 rounded-xl border px-3.5 py-2.5 cursor-pointer transition-all duration-200 ${
                        showOnlySelectedYear
                          ? "border-sky-200 bg-sky-50 text-sky-900 shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-sky-50/60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={showOnlySelectedYear}
                        onChange={() => setShowOnlySelectedYear((prev) => !prev)}
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span className="text-sm font-semibold">
                        {language === "de"
                          ? `Nur Steuerjahr ${yearLabel} anzeigen`
                          : `Show only tax year ${yearLabel}`}
                      </span>
                    </label>

                    <div className="mt-2 pl-1">
                      {showOnlySelectedYear ? (
                        <div className="flex flex-col gap-1">
                          <span className="block text-xs font-medium text-slate-600 whitespace-nowrap overflow-hidden text-ellipsis">
                            {hiddenByYearCount > 0
                              ? language === "de"
                                ? `${formatCount(hiddenByYearCount)} außerhalb ${yearLabel} ausgeblendet`
                                : `${formatCount(hiddenByYearCount)} hidden outside ${yearLabel}`
                              : language === "de"
                                ? `Nur Transfers aus ${yearLabel}`
                                : `Only transfers from ${yearLabel}`}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            {language === "de"
                              ? "Die komplette Historie kann jederzeit für FIFO- und Klassifizierungsprüfung eingeblendet werden."
                              : "The full history can be revealed at any time for FIFO and classification review."}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs font-medium text-slate-600">
                          {language === "de"
                            ? "Komplette Historie eingeblendet"
                            : "Full history visible"}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-sm font-medium text-slate-600">
                    {language === "de"
                      ? "Kein Steuerjahr gesetzt – komplette Historie sichtbar"
                      : "No tax year selected – full history visible"}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4 items-center text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showVetVthoData}
                    onChange={() => setShowVetVthoData((v) => !v)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-slate-700">
                    {language === "de" ? "VET/VTHO Daten anzeigen" : "Show VET/VTHO data"}
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showVip180Data}
                    onChange={() => setShowVip180Data((v) => !v)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-slate-700">
                    {language === "de" ? "VIP-180-Daten anzeigen" : "Show VIP-180 data"}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className={cn(T.toolbar, "mt-4 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
            <div className="flex-1 min-w-0 w-full">
            <input
              type="text"
              placeholder={`🔍 ${t("search", language)} ${t("token", language)}, ${t(
                "transactionHash",
                language
              )}, ${t("address", language)}, ${t("amount", language)}...`}
              className={T.input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto justify-between xl:justify-end">
            <span className={T.countPill}>
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-semibold text-slate-800">{filtered.length}</span>
              <span>{t("transactions", language)}</span>
            </span>

            <div className="flex gap-2 flex-wrap">
              <select
                className={T.select}
                value={tokenFilter}
                onChange={(e) => setTokenFilter(e.target.value)}
              >
                <option value="ALL">🔁 {t("allTokens", language)}</option>
                {availableTokens.map((token) => (
                  <option key={token} value={token}>
                    {token}
                  </option>
                ))}
              </select>

              <select
                className={T.select}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="ALL">🔎 {t("allTypes", language)}</option>

                <option value="received">⬅️ {t("received", language)}</option>
                <option value="sent">➡️ {t("sent", language)}</option>
                <option value="swap_in">🔄 Swap In</option>
                <option value="swap_out">🔄 Swap Out</option>
                <option value="swap_refund_in">🟢🔄 Swap Refund In</option>
                <option value="golden_empire_reward">🏰 Golden Empire Reward</option>
                <option value="golden_empire_spend">🏰 Golden Empire Spend</option>
                <option value="golden_empire_burn_spend">🏰 Golden Empire Burn Spend</option>
                <option value="nfbc_purchase">📚 NFBC Purchase</option>
                <option value="liquidity_add">🧩 Add Liquidity (LP)</option>
                <option value="liquidity_remove">🧩 Remove Liquidity (LP)</option>
                <option value="internal_transfer">↔️ Interner Transfer</option>
                <option value="refund">↩️ Refund</option>

                <option value="nft_mint">🎨 NFT Mint</option>
                <option value="NFT_Galaxy_mint">🎨 NFT Galaxy Mint</option>
                <option value="sale_payment">💰 Sale Payment</option>
                <option value="stargate_deposit">🔒 Stargate Deposit</option>
                <option value="stargate_staking_rewards">Stargate Staking Rewards</option>
                <option value="stargate_boosted_maturity">Stargate Boosted Maturity</option>
                <option value="thorhead_staking_rewards">Thorhead Staking Rewards</option>
                <option value="vemugs_staking_rewards">VeMugs Staking Rewards</option>
                <option value="vemugs_mint_reward">VeMugs Mint Reward</option>
                <option value="vemugs_reward_distribution">VeMugs Reward Distribution</option>
                <option value="vemugs_mint_gas_refund">VeMugs Mint Gas Refund</option>
                <option value="vemugs_mint_zero_transfer">VeMugs Mint 0 VTHO Placeholder</option>
                <option value="wov_genesis_staking_rewards">World of V Staking Rewards</option>
                <option value="wov_staking_rewards">World of V Staking Rewards (Legacy)</option>
                <option value="nft_creator_royalty">World of V Creator Royalty</option>
                <option value="wov_restake">World of V Restake</option>
                <option value="wov_claim_fee">World of V Claim Fee</option>
                <option value="nft_marketplace_vet_bid_deposit">
                  🌐 {language === "de" ? "World of V Auktionsgebot hinterlegt" : "World of V Auction Bid Deposit"}
                </option>
                <option value="nft_marketplace_vet_bid_refund">
                  🌐 {language === "de" ? "World of V Auktionsgebot zurückerstattet" : "World of V Auction Bid Refund"}
                </option>
                <option value="pause_your_carbon_carbon_years">🌱 Pause Your Carbon Carbon Years</option>
                <option value="solarwise_electricity_revenue">☀️ Solarwise Electricity Revenue</option>
                <option value="solarwise_stake_deposit">Solarwise Stake Deposit</option>
                <option value="solarwise_stake_withdrawal">Solarwise Stake Withdrawal</option>
                <option value="vet_stake_deposit">
                  🔒 {language === "de" ? "VET Stake Einzahlung" : "VET Stake Deposit"}
                </option>
                <option value="vet_stake_withdrawal">
                  🔓 {language === "de" ? "VET Stake Auszahlung" : "VET Stake Withdrawal"}
                </option>
                <option value="node_legacy_rewards">📡 Legacy Node Rewards</option>
                <option value="foundation_bonus">🏛️🔄 Foundation Bonus/Swap</option>
                <option value="vexchange_farm_rewards">🟦 Vexchange Farm Rewards</option>
                <option value="mva_staking_rewards">🎯 MVA Staking Rewards</option>
                <option value="verocket_generated_vtho_claim">🟦 VeRocket VTHO Claim</option>

                <option value="x2earn_reward_claim">X2Earn Reward · Claim</option>
                <option value="wanchain_bridge_to_earn_reward">Wanchain Bridge-to-Earn Reward</option>
                <option value="bangzboardz_staking_reward">BangzBoardz Staking Reward</option>
                <option value="goatz_milk_nft_holder_reward">Goatz Club · MILK NFT Holder Reward</option>
                <option value="b3mo">B3MO</option>
                <option value="b3mo_challenge_escrow_deposit">B3MO · Challenge Deposit</option>
                <option value="b3mo_challenge_principal_return">B3MO · Principal Return</option>
                <option value="b3mo_challenge_payout_reward">B3MO · Challenge Reward</option>
                <option value="b3mo_split_win_reward">B3MO · SplitWin Reward</option>
                <option value="vet_domains">vet.domains</option>
                <option value="vet_domains_registration">vet.domains · Registration</option>
                <option value="vet_domains_renewal">vet.domains · Renewal</option>
                <option value="vet_domains_payment_refund">vet.domains · Payment Refund</option>
                <option value="vaultx">VaultX</option>
                <option value="vaultx_loan_borrow">VaultX · Loan Borrow</option>
                <option value="vaultx_collateral_deposit">VaultX · Collateral Deposit</option>
                <option value="vaultx_collateral_return">VaultX · Collateral Return</option>
                <option value="vaultx_loan_principal_repayment">VaultX · Principal Repayment</option>
                <option value="vaultx_loan_repayment_fee">VaultX · Repayment Fee</option>
                <option value="vaultx_loan_repayment_refund">VaultX · Refund</option>
                <option value="vaultx_auction_bid_deposit">VaultX · Auction Bid</option>
                <option value="vaultx_auction_bid_refund">VaultX · Auction Bid Refund</option>
                <option value="vaultx_protocol_outflow_review">VaultX · Protocol Outflow Review</option>
                <option value="vaultx_nft_return_review">VaultX · NFT Return Review</option>
                <option value="safehaven_staking_rewards">🛡️ Safehaven Staking Rewards</option>
                <option value="sagaz_reward_claim">🟦 Sagaz Reward Claim</option>
                <option value="vaiyans_vco_game_entry">Vaiyans VCO Game Entry</option>
                <option value="vaiyans_vco_prediction_entry">Vaiyans VCO Prediction Entry</option>
                <option value="vaiyans_vco_leaderboard_reward">Vaiyans VCO Leaderboard Reward</option>
                <option value="vaiyans_vco_tip">Vaiyans VCO Game Entry (Legacy)</option>
                <option value="scoopup_puppy_pass_fee">ScoopUp · Puppy Pass</option>
                <option value="one_million_pixel_purchase">
                  1Million Pixel · Pixel Purchase
                </option>
                <option value="velottery_ticket_purchase">VeLottery · Paid Ticket</option>
                <option value="squad_game_spend">🐢 Squirtle Squad Game Spend</option>
                <option value="squad_vtho_game_entry">🐢 Squirtle Squad VTHO Game Entry</option>
                <option value="squad_nft_upgrade_spend">🐢 Squirtle Squad NFT Upgrade Spend</option>
                <option value="squad_nft_upgrade_burn">🐢 Squirtle Squad NFT Upgrade Burn</option>
                <option value="squad_nft_upgrade_mint">🐢 Squirtle Squad NFT Upgrade Mint</option>
                <option value="squad_preico_payment">🐢 Squirtle Squad Pre-ICO Payment</option>
                <option value="squad_preico_forward">🐢 Squirtle Squad Pre-ICO Forward</option>
                <option value="squad_preico_claim">🐢 Squirtle Squad Pre-ICO Claim</option>
                <option value="squad_game_reward">🐢 Squirtle Squad Game Reward</option>
                <option value="vfox_nft_holder_reward">🦊 VFA NFT Holder Reward</option>
                <option value="vesea_nft_staking_rewards">🌊 VeSea NFT Staking Rewards</option>
                <option value="union_nft_holder_rewards">💊 UNION NFT Holder Rewards</option>
                <option value="vimworld_veed_reward_distribution">🟦 VIMworld Reward Distribution</option>
                <option value="vimworld_veed_deposit">🟦 VIMworld VEED Deposit</option>
                <option value="vimworld_veed_withdrawal">🟦 VIMworld VEED Withdrawal</option>
                <option value="vimworld_veed_feed_fee">🟦 VIMworld VEED Feed Fee</option>
                <option value="vimworld_veed_withdrawal_fee">🟦 VIMworld VEED Withdrawal Fee</option>
                <option value="Governance_reward">🗳 Governance_reward</option>
                <option value="governance_deposit">🗳️ Governance Deposit</option>
                <option value="governance_refund">🗳️ Governance Refund</option>
                <option value="mint_payment">🎨 NFT Mint</option>
                <option value="Governance_delegate_fee">🧾 Governance Delegate Fee</option>

                <option value="B3TR_VOT3_wrap">B3TR_VOT3_wrap</option>
                <option value="VOT3_B3TR_wrap">VOT3_B3TR_unwrap</option>
                <option value="B3TR_VEB3TR_wrap">B3TR_VEB3TR_wrap</option>
                <option value="VEB3TR_B3TR_wrap">VEB3TR_B3TR_unwrap</option>
                <option value="VOT3_VEB3TR_wrap">VOT3_VEB3TR_wrap</option>
                <option value="VEB3TR_VOT3_wrap">VEB3TR_VOT3_unwrap</option>

                <option value="VET_VVET_wrap">VET_VVET_wrap</option>
                <option value="VVET_VET_unwrap">VVET_VET_unwrap</option>
                <option value="VET_WVET_wrap">VET_WVET_wrap</option>
                <option value="WVET_VET_unwrap">WVET_VET_unwrap</option>
                <option value="VET_BVET_wrap">VET_BVET_wrap</option>
                <option value="BVET_VET_unwrap">BVET_VET_unwrap</option>

                <option value="exchange_withdrawal">↔️ Exchange_withdrawal</option>
                <option value="exchange_deposit">↔️ Exchange_deposit</option>

                {JUICY_RAW_TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}

                <option value="unknown">❓ {t("unknown", language)}</option>
              </select>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Status */}
      {filtered.length === 0 && (
        <div className="mx-6 my-6 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-700 shadow-sm">
          <div>
            {pdfExportMode
              ? t("noTransactionsFound", language)
              : <>🚫 {t("noTransactionsFound", language)}</>}
          </div>

          {!pdfExportMode && showOnlySelectedYear && hasSelectedYear && hiddenByYearCount > 0 && (
            <div className="mt-1 text-xs not-italic text-slate-500">
              {language === "de"
                ? `${formatCount(hiddenByYearCount)} Transfers außerhalb ${yearLabel} sind derzeit ausgeblendet.`
                : `${formatCount(hiddenByYearCount)} transfers outside ${yearLabel} are currently hidden.`}
            </div>
          )}
        </div>
      )}

      {/* Tabelle */}
      {filtered.length > 0 && (
        <div className={T.tableBlock}>
          <div className={cn(T.tableWrap, primaryTableWrapClass, "print:overflow-visible")}>
          {pdfExportMode ? (
            pdfRawChunks.map((chunk, chunkIndex) => (
              <table
                key={`raw-pdf-page-${chunkIndex}`}
                className={cn(
                  T.table,
                  "v3-pdf-raw-table min-w-0 table-fixed",
                  chunkIndex > 0 && "v3-pdf-raw-page-table"
                )}
              >
                {renderRawTransfersThead()}
                <tbody className={T.tbody}>
                  {chunk.map((tx, index) => {
                    const realIndex = chunkIndex * RAW_PDF_ROWS_PER_PAGE + index;
                    return renderRawTransfersRow(tx, realIndex);
                  })}
                </tbody>
              </table>
            ))
          ) : (() => {
            const virtualRows = rowVirtualizer.getVirtualItems();
            const padTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
            const padBottom =
              virtualRows.length > 0
                ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
                : 0;
            const colSpan = 8;

            return (
              <div
                ref={tableScrollRef}
                className={cn(
                  rawTableScrollHeightClass,
                  "overflow-auto overscroll-contain v3-raw-scroll"
                )}
              >
                <table className={cn(T.table, "min-w-[1160px] table-fixed print:min-w-0")}>
                  <thead className={cn(T.thead, primaryTableHeadClass, "sticky top-0 z-10 bg-white")}>
                    <tr>
                      <th className={cn(T.th, "text-left", isEditMode ? "raw-type-col-edit w-[14%] max-w-0" : "w-[14%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[7%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("source", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[7%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[9%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[14%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("recipient", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[14%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("sender", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[20%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
                        </span>
                      </th>
                      <th className={cn("raw-date-col", T.th, "text-center w-[11%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={T.tbody}>
                    {padTop > 0 ? (
                      <tr aria-hidden="true">
                        <td colSpan={colSpan} style={{ height: padTop, padding: 0, border: 0 }} />
                      </tr>
                    ) : null}
                    {virtualRows.map((virtualRow) =>
                      renderRawTransfersRow(filtered[virtualRow.index], virtualRow.index)
                    )}
                    {padBottom > 0 ? (
                      <tr aria-hidden="true">
                        <td colSpan={colSpan} style={{ height: padBottom, padding: 0, border: 0 }} />
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            );
          })()}
          </div>
        </div>
      )}
    </section>
  );
}
